import type { FlowDocument } from '@segmently/schema-models/v2';
import type { StrategyBlockInstruction } from '@segmently/schema-models/strategy';
import {
  appendTriggerEdgeAtActionKeyInPlace,
  collectShadowedTriggerEdges,
  collectTriggerEdges,
  ensureTerminalActionInPlace,
  hasTerminalPreferredTrigger,
  remapConditionKey,
  remapConditionKeysInPlace,
  remapTriggerTargetIdsInPlace,
  rewriteTriggerEdgesInPlace,
  type TriggerEdgeLike,
} from '../../screen-utils/services/triggerEdgeWalker';
import { calculateBlockPositions } from './blockPositionCalculator';
import { computeCompiledBlockPlacements, COMPILED_LAYOUT_VERSION } from './compiledBlockPlacement';
import {
  TASK_ROUTER_VARIABLE_ID,
  buildTaskRouterVariableForBlocks,
  resolveDisplayText,
} from './taskRouterVariable';
import {
  createStrategySchemaBlockAction,
  getBlockEdgeTarget,
  getBlockEntityId,
  normalizeBlockActionToStrategySchema,
  type BlockEdgeLike,
  type StrategyBlockLike,
} from './blockNavigationEdges';
import {
  applyVariableResolutions,
  diffFunnelVariables,
  rewriteVariableRefsInScreens,
  type VariableDiffResult,
  type VariableForkRecord,
  type VariableResolutionAction,
} from './strategyVariableDiff';
import {
  diffAiAssetSlots,
  mergeAiAssetSlots,
  type AiAssetSlotDiffEntry,
  type AiAssetSlotResolution,
} from '../../screen-utils/services/aiAssetSyncDiff';
import { applyAiAssetMirrorOpToStep } from '../../screen-utils/services/aiAssetSlotPaths';
import {
  materializeEmbedThemeDefaults,
  materializeInputConstraints,
  materializeItemLayoutSubtitles,
  materializePaywallPriceLabels,
  materializePurchaseActionIntegrity,
} from './compiledScreenMaterializers';

export const STRATEGY_BLOCKS_MANIFEST_METADATA_KEY = 'segmently.strategyBlocks.manifest.v1';

/**
 * Generation-context blobs that made the transform manifest exceed the
 * Firestore ~1MiB version-document ceiling (a 20-block strategy produced a
 * 1.46MB manifest; these two keys were 92% of it). They are NEVER read from
 * the manifest — the strategy block documents remain their source of truth —
 * so the manifest stores an `omittedHeavyKeys` marker instead and the lift
 * write path carries the values over from the existing block doc.
 */
export const MANIFEST_HEAVY_BLOCK_DATA_KEYS = ['personalizationContext', 'blockContract'] as const;
export const MANIFEST_OMITTED_HEAVY_KEYS_FIELD = 'omittedHeavyKeys';
export const STRATEGY_TAG_PREFIX = 'strategy:';
export const STRATEGY_BLOCK_TAG_PREFIX = 'block:';
export const STRATEGY_BLOCK_DOC_TAG_PREFIX = 'block-doc:';

export interface StrategyFlowScreen extends Record<string, unknown> {
  id?: string;
  entityId?: string;
  name?: string;
  screenType?: string;
  content?: Record<string, unknown>;
  position?: { x: number; y: number };
  tags?: string[];
  aiMeta?: Record<string, unknown>;
}

export interface StrategyFlowBlock extends StrategyBlockLike, Record<string, unknown> {
  id?: string;
  entityId?: string;
  name?: string;
  firstScreenId?: string;
  screens?: StrategyFlowScreen[];
  position?: { x: number; y: number };
  customSubtype?: string;
  instructions?: StrategyBlockInstruction[];
  compileHints?: StrategyBlockCompileHints;
}

export interface StrategyBlockScreenBundle {
  block: StrategyFlowBlock;
  screens?: StrategyFlowScreen[];
}

export interface StrategyBlockCompileHints {
  entryScreenId?: string;
  exitScreenId?: string;
  exitActionKey?: string;
  /**
   * ALL exit screens of the block (B1-W5): screens with aiMeta.screenRole
   * 'exit', else every intra-block leaf. exits[0] is always the legacy
   * `exitScreenId` pick. Boundary edges without an explicit sourceScreenId
   * replicate onto EVERY exit's carrier — sibling exit variants stop
   * dead-ending (B0 baseline: 15 stranded variants).
   */
  exits?: Array<{ screenId: string; actionKey?: string }>;
}

interface BlockBoundaryTarget {
  targetEntityId: string;
  sourceScreenId?: string;
  sourceActionKey?: string;
  targetScreenId?: string;
  transitionKind?: string;
  conditions?: Array<Record<string, unknown>>;
}

export interface StrategyBlockManifestItem {
  blockId?: string;
  blockEntityId: string;
  name?: string;
  blockType?: string;
  customSubtype?: string;
  instructions?: StrategyBlockInstruction[];
  order?: number;
  taskId?: string | null;
  isFirstBlock?: boolean;
  isLaunchBlock?: boolean;
  firstScreenId?: string;
  entryScreenId?: string;
  exitScreenId?: string;
  screenIdMap: Record<string, string>;
  compileHints?: StrategyBlockCompileHints;
  blockData?: Record<string, unknown>;
}

export interface StrategyFlowManifest {
  version: 1;
  /**
   * Block-placement layout generation (B1-W2). `2` = extent-aware packing with
   * exact per-block `blockData.compiledOrigin`; absent = legacy `x*4/y*3`
   * origin shift. `version` stays 1 — parseManifest hard-checks it and a bump
   * would orphan every already-compiled funnel.
   */
  layoutVersion?: number;
  strategyId: string;
  strategyName?: string;
  generatedAt: string;
  blocks: StrategyBlockManifestItem[];
  /**
   * C9-S4: blocks skipped by `--exclude-unreachable` at compile time. They stay
   * on the strategy; push default-skips them (a scoped blocks push re-includes
   * one), and an absorb-lift never deletes them from the strategy.
   */
  excludedBlockEntityIds?: string[];
}

export interface StrategyFlattenInput {
  strategyId: string;
  strategyName?: string;
  blocks: Array<StrategyFlowBlock | StrategyBlockScreenBundle>;
  defaultLanguage?: string;
  languages?: string[];
  variables?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** C9-S4: skip blocks not reachable from the launch block over block edges. */
  excludeUnreachable?: boolean;
}

export interface StrategyFlattenResult {
  flowDocument: FlowDocument & { metadata: Record<string, string>; variables?: Record<string, unknown> };
  manifest: StrategyFlowManifest;
  warnings: string[];
  /**
   * C9-S6: variable ids whose definitions/bindings were synthesized from
   * aiMeta.variableRealizations because the strategy lacked them (defect #2).
   * A non-dry compile writes these back to strategy.variables so the next
   * compile finds them declared and synthesizes nothing.
   */
  filledVariableIds: string[];
}

export interface StrategyLiftInput {
  strategyId?: string;
  strategyName?: string;
  flowDocument: FlowDocument | {
    schemaVersion?: string;
    defaultLanguage?: string;
    languages?: string[];
    launchScreenId?: string;
    screens?: Record<string, StrategyFlowScreen>;
    metadata?: Record<string, unknown>;
    variables?: Record<string, unknown>;
  };
}

export interface StrategyLiftLooseScreen {
  /** Global screen id in the FlowDocument. */
  screenId: string;
  screenName?: string;
  screenType?: string;
  /**
   * Blocks this loose screen most likely belongs to, ranked by the number of
   * trigger edges connecting it (either direction) to screens with block
   * membership. At most 3; ties break by block order in the manifest.
   */
  suggestedBlockEntityIds?: string[];
}

export interface StrategyLiftForeignTagIgnored {
  screenId: string;
  blockTag: string;
  strategyTag: string;
}

export interface StrategyLiftResult {
  blocks: StrategyFlowBlock[];
  manifest?: StrategyFlowManifest;
  warnings: string[];
  /** Screens grouped under the synthetic `_untagged` "Loose Screens" block. */
  looseScreens?: StrategyLiftLooseScreen[];
  /** Screens whose block tag was ignored because they belong to another strategy. */
  foreignTagIgnored?: StrategyLiftForeignTagIgnored[];
}

interface PreparedBlock {
  block: StrategyFlowBlock;
  blockId?: string;
  blockEntityId: string;
  screens: StrategyFlowScreen[];
  localToGlobal: Map<string, string>;
  globalToLocal: Map<string, string>;
  entryLocalId?: string;
  entryGlobalId?: string;
  exitLocalId?: string;
  exitGlobalId?: string;
  /** All exit screens (B1-W5); [0] is the legacy exitLocalId pick. */
  exitLocalIds: string[];
  exitGlobalIds: string[];
}

export function normalizeStrategyBlocksForFlowTransform(
  blocks: StrategyFlowBlock[],
  strategyId: string,
): StrategyFlowBlock[] {
  return blocks.map((block, blockIndex) => {
    const blockEntityId = getBlockEntityId(block) || `block_${blockIndex + 1}`;
    const screens = Array.isArray(block.screens) ? block.screens : [];
    const firstScreenId = typeof block.firstScreenId === 'string' ? block.firstScreenId : undefined;
    const exitScreenId = inferExitScreenId(screens, firstScreenId);
    const compileHints = compactCompileHints({
      ...(isRecord(block.compileHints) ? block.compileHints : {}),
      entryScreenId: firstScreenId,
      exitScreenId,
    });

    screens.forEach((screen, screenIndex) => {
      const localScreenId = getScreenId(screen) || `screen_${screenIndex + 1}`;
      screen.id = localScreenId;
      screen.tags = mergeTags(screen.tags, [
        `${STRATEGY_TAG_PREFIX}${strategyId}`,
        `${STRATEGY_BLOCK_TAG_PREFIX}${blockEntityId}`,
        ...(block.id ? [`${STRATEGY_BLOCK_DOC_TAG_PREFIX}${block.id}`] : []),
      ]);
      screen.aiMeta = {
        ...(isRecord(screen.aiMeta) ? screen.aiMeta : {}),
        strategyId,
        blockEntityId,
        // Explicit-bake snapshots store blocks WITHOUT an inline `id` field
        // (auto-bake keeps it) — an unconditional `blockDocId: block.id` wrote
        // `undefined` into screensGraph and Firestore rejected the version
        // ("invalid nested entity"). Guard like the tags line above.
        ...(block.id ? { blockDocId: block.id } : {}),
        localScreenId,
        screenRole: inferScreenRole(localScreenId, firstScreenId, exitScreenId),
      };
    });

    return {
      ...block,
      entityId: blockEntityId,
      compileHints,
      screens,
    };
  });
}

/* ── Defect #2: runtime variable bindings must survive compile ─────────────
 * Strategy variables reference LOCAL screen ids (boundScreenIds /
 * screenBindings / screenScoreEffects keys) while compile re-keys screens
 * local→global — previously the record was copied verbatim, orphaning every
 * binding (engine records nothing, conditions never match). Two pure
 * helpers fix it: remap existing refs via the per-block id maps, and
 * synthesize missing bindings from the generator's
 * aiMeta.variableRealizations provenance (port of the MEL normalize repair).
 */

const screenRefKeys = ['screenBindings', 'screenScoreEffects'] as const;

function collectVariableScreenRefs(variable: Record<string, unknown>): Set<string> {
  const refs = new Set<string>();
  if (Array.isArray(variable.boundScreenIds)) {
    for (const id of variable.boundScreenIds) {
      if (typeof id === 'string' && id) refs.add(id);
    }
  }
  for (const key of screenRefKeys) {
    if (isRecord(variable[key])) {
      for (const id of Object.keys(variable[key] as Record<string, unknown>)) refs.add(id);
    }
  }
  return refs;
}

function resolveVariableScreenIdMap(
  refIds: Set<string>,
  prepared: PreparedBlock[],
  variableId: string,
  warnings: string[],
): Map<string, string> {
  const mapping = new Map<string, string>();
  for (const localId of refIds) {
    const hits = prepared.filter((block) => block.localToGlobal.has(localId));
    if (hits.length === 0) {
      // Already-global ids (e.g. a re-compiled record) pass through silently.
      const isGlobal = prepared.some((block) => block.globalToLocal.has(localId));
      if (!isGlobal) {
        warnings.push(
          `Variable ${variableId}: screen ref "${localId}" not found in any block — kept verbatim (defect #2 guard).`,
        );
      }
      continue;
    }
    if (hits.length === 1) {
      mapping.set(localId, hits[0].localToGlobal.get(localId) as string);
      continue;
    }
    // Cross-block local-id collision: prefer the block covering most of this
    // variable's other refs, loudly.
    const best = hits
      .map((block) => ({
        block,
        cover: [...refIds].filter((id) => block.localToGlobal.has(id)).length,
      }))
      .sort((a, b) => b.cover - a.cover)[0].block;
    warnings.push(
      `Variable ${variableId}: screen ref "${localId}" exists in ${hits.length} blocks — mapped via block ${best.blockEntityId} (best coverage).`,
    );
    mapping.set(localId, best.localToGlobal.get(localId) as string);
  }
  return mapping;
}

function remapVariableScreenRefs(
  variables: Record<string, unknown> | undefined,
  prepared: PreparedBlock[],
): { variables: Record<string, unknown>; warnings: string[] } {
  const warnings: string[] = [];
  const result: Record<string, unknown> = {};
  if (!isRecord(variables)) return { variables: result, warnings };

  for (const [variableId, raw] of Object.entries(variables)) {
    if (!isRecord(raw)) {
      result[variableId] = raw;
      continue;
    }
    const variable = cloneJson(raw) as Record<string, unknown>;
    const mapping = resolveVariableScreenIdMap(collectVariableScreenRefs(variable), prepared, variableId, warnings);
    if (Array.isArray(variable.boundScreenIds)) {
      variable.boundScreenIds = variable.boundScreenIds.map((id) =>
        typeof id === 'string' ? mapping.get(id) ?? id : id,
      );
    }
    for (const key of screenRefKeys) {
      if (isRecord(variable[key])) {
        variable[key] = Object.fromEntries(
          Object.entries(variable[key] as Record<string, unknown>).map(([screenId, value]) => [
            mapping.get(screenId) ?? screenId,
            value,
          ]),
        );
      }
    }
    result[variableId] = variable;
  }
  return { variables: result, warnings };
}

interface SynthesizedVariableWarning {
  variableId?: string;
  message: string;
}

const itemTitle = (item: Record<string, unknown>, locale: string): string | undefined => {
  const title = isRecord(item.title) ? item.title : undefined;
  const translations = title && isRecord(title.translations) ? (title.translations as Record<string, unknown>) : undefined;
  const localized = translations?.[locale] ?? Object.values(translations ?? {})[0];
  return typeof localized === 'string' ? localized : undefined;
};

/** Pure port of the MEL normalize repair (simulate.ts buildRealizationVariables):
 * rebuild collect bindings from aiMeta.variableRealizations. flowScreens are
 * already keyed by GLOBAL id here — no remap needed. */
function synthesizeVariablesFromRealizations(
  flowScreens: Record<string, StrategyFlowScreen>,
  locale: string,
): { variables: Record<string, Record<string, unknown>>; warnings: SynthesizedVariableWarning[] } {
  const warnings: SynthesizedVariableWarning[] = [];
  const merged = new Map<string, Record<string, unknown>>();
  const mergeEntry = (variableId: string, screenId: string, entry: Record<string, unknown>): void => {
    const previous = merged.get(variableId);
    if (!previous) {
      merged.set(variableId, entry);
      return;
    }
    const previousScreenIds = Array.isArray(previous.boundScreenIds) ? previous.boundScreenIds : [];
    previous.boundScreenIds = [...new Set([...previousScreenIds, screenId])];
    if (isRecord(entry.screenBindings)) {
      previous.screenBindings = {
        ...(isRecord(previous.screenBindings) ? previous.screenBindings : {}),
        ...entry.screenBindings,
      };
    }
    warnings.push({ variableId, message: `MERGED multi-screen variable ${variableId}` });
  };

  for (const [screenId, screen] of Object.entries(flowScreens)) {
    const realizations = isRecord(screen.aiMeta) && Array.isArray(screen.aiMeta.variableRealizations)
      ? (screen.aiMeta.variableRealizations as Array<Record<string, unknown>>)
      : [];
    const real = realizations.find((entry) => isRecord(entry) && entry.realizationKind === 'collect');
    if (!real || typeof real.variableId !== 'string') continue;
    const variableId = real.variableId;

    if (screen.screenType === 'TextInput') {
      const content = isRecord(screen.content) ? screen.content : {};
      const textField = isRecord(content.textField) ? content.textField : {};
      const type = textField.fieldType === 'number' ? 'number' : 'string';
      mergeEntry(variableId, screenId, {
        id: variableId,
        name: variableId,
        type,
        required: false,
        scope: 'screen',
        boundScreenIds: [screenId],
      });
      continue;
    }

    if (screen.screenType !== 'ListSinglePick' && screen.screenType !== 'ListMultiPick') {
      warnings.push({
        variableId,
        message: `SKIP ${screenId} (${screen.screenType}) — non-list collect screen not bindable (known cap)`,
      });
      continue;
    }
    const content = isRecord(screen.content) ? screen.content : {};
    const options = isRecord(content.options) ? content.options : {};
    const items = Array.isArray(options.items) ? (options.items as Array<Record<string, unknown>>) : [];
    if (!items.length) {
      warnings.push({ variableId, message: `SKIP ${screenId} — no options.items` });
      continue;
    }

    const optionIds = Array.isArray(real.optionIds)
      ? (real.optionIds as unknown[]).filter((id): id is string => typeof id === 'string')
      : [];
    const bindCount = Math.min(items.length, optionIds.length || items.length);
    if (optionIds.length && optionIds.length !== items.length) {
      warnings.push({
        variableId,
        message: `LENGTH MISMATCH ${screenId}: ${optionIds.length} realization optionIds vs ${items.length} items — binding first ${bindCount}`,
      });
    }
    const ids = optionIds.length ? optionIds : items.map((item, index) => String(item.variableValue ?? index));

    const bindings: Record<string, string> = {};
    const optionEntries: Array<{ id: string; label: string }> = [];
    for (let i = 0; i < bindCount; i += 1) {
      const variableValue = String(items[i].variableValue ?? ids[i]);
      bindings[ids[i]] = variableValue;
      optionEntries.push({ id: ids[i], label: itemTitle(items[i], locale) ?? variableValue });
    }

    const entry: Record<string, unknown> = {
      id: variableId,
      name: variableId,
      type: screen.screenType === 'ListMultiPick' ? 'enum[]' : 'enum',
      required: false,
      scope: 'screen',
      options: optionEntries,
      boundScreenIds: [screenId],
      screenBindings: { [screenId]: bindings },
    };
    mergeEntry(variableId, screenId, entry);
  }

  return { variables: Object.fromEntries(merged), warnings };
}

/** V2 screen types whose value is typed (and therefore can be left empty). */
const REQUIRED_GATE_SCREEN_TYPES = new Set(['TextInput']);
const REQUIRED_INPUT_VISIBILITY = 'disabledUntilValueEntered';

/**
 * D-T9a-8. Stamps `actionBar.primary.appearance.visibility =
 * 'disabledUntilValueEntered'` on every text-input screen that collects a
 * REQUIRED variable. Returns one warning per stamp so the compile stays
 * auditable.
 */
function stampRequiredInputGates(
  flowScreens: Record<string, StrategyFlowScreen>,
  variables: Record<string, unknown>,
): string[] {
  const requiredVariableIds = new Set<string>();
  const requiredByScreenId = new Map<string, string>();
  for (const [variableId, definition] of Object.entries(variables)) {
    if (!isRecord(definition) || definition.required !== true) continue;
    requiredVariableIds.add(variableId);
    const bound = Array.isArray(definition.boundScreenIds) ? definition.boundScreenIds : [];
    for (const screenId of bound) {
      if (typeof screenId === 'string' && screenId && !requiredByScreenId.has(screenId)) {
        requiredByScreenId.set(screenId, variableId);
      }
    }
  }
  if (requiredVariableIds.size === 0) return [];

  const warnings: string[] = [];
  for (const [screenId, screen] of Object.entries(flowScreens)) {
    if (!REQUIRED_GATE_SCREEN_TYPES.has(screen.screenType ?? '')) continue;
    const content = isRecord(screen.content) ? screen.content : null;
    if (!content || !isRecord(content.textField)) continue;

    // `boundScreenIds` is the declaration; aiMeta realizations are the
    // generator's own record and survive a stale/never-remapped binding.
    const variableId = requiredByScreenId.get(screenId)
      ?? collectRealizationVariableId(screen, requiredVariableIds);
    if (!variableId) continue;

    const actionBar = isRecord(content.actionBar) ? content.actionBar : null;
    const primary = actionBar && isRecord(actionBar.primary) ? actionBar.primary : null;
    // No primary CTA to gate — screens.primaryCtaMissingLabel owns that shape.
    if (!primary) continue;

    const appearance = isRecord(primary.appearance) ? primary.appearance : {};
    const current = appearance.visibility;
    if (typeof current === 'string' && current && current !== 'default') continue;
    if (current === REQUIRED_INPUT_VISIBILITY) continue;

    primary.appearance = { ...appearance, visibility: REQUIRED_INPUT_VISIBILITY };
    warnings.push(
      `Screen ${screenId}: required variable ${variableId} — primary CTA gated with `
      + `${REQUIRED_INPUT_VISIBILITY} so an empty answer cannot advance (D-T9a-8).`,
    );
  }
  return warnings;
}

function collectRealizationVariableId(
  screen: StrategyFlowScreen,
  requiredVariableIds: Set<string>,
): string | null {
  const aiMeta = isRecord(screen.aiMeta) ? screen.aiMeta : null;
  const realizations = aiMeta && Array.isArray(aiMeta.variableRealizations)
    ? aiMeta.variableRealizations
    : [];
  for (const entry of realizations) {
    if (!isRecord(entry) || entry.realizationKind !== 'collect') continue;
    const variableId = entry.variableId;
    if (typeof variableId === 'string' && requiredVariableIds.has(variableId)) return variableId;
  }
  return null;
}

/** True when a list binding or scalar input binding can resolve at runtime. */
function hasResolvableBindings(variable: unknown): boolean {
  if (!isRecord(variable)) return false;
  if (isRecord(variable.screenBindings) && Object.values(variable.screenBindings).some(
    (bindings) => isRecord(bindings) && Object.keys(bindings).length > 0,
  )) return true;
  return (variable.type === 'string' || variable.type === 'number')
    && Array.isArray(variable.boundScreenIds)
    && variable.boundScreenIds.some((screenId) => typeof screenId === 'string' && screenId.length > 0);
}

/**
 * Router-grade binding check (B1-W4): bindings resolve only if the bound
 * screen EXISTS in the compiled document and every binding VALUE is still one
 * of that screen's option values — the runtime looks bindings up by the
 * picked option's variableValue, so a renamed option silently kills routing.
 */
function routerBindingsResolvable(
  variable: unknown,
  flowScreens: Record<string, StrategyFlowScreen>,
  expectedTaskIds: string[],
): boolean {
  if (!hasResolvableBindings(variable)) return false;
  const screenBindings = (variable as { screenBindings: Record<string, unknown> }).screenBindings;
  const boundTaskIds = new Set<string>();
  for (const [screenId, binding] of Object.entries(screenBindings)) {
    if (!isRecord(binding) || Object.keys(binding).length === 0) continue;
    const screen = flowScreens[screenId];
    if (!screen) return false;
    const optionValues = collectScreenOptionValues(screen);
    for (const [taskId, value] of Object.entries(binding)) {
      if (typeof value !== 'string' || !optionValues.has(value)) return false;
      boundTaskIds.add(taskId);
    }
  }
  return expectedTaskIds.every((taskId) => boundTaskIds.has(taskId));
}

function collectScreenOptionValues(screen: StrategyFlowScreen): Set<string> {
  const values = new Set<string>();
  const content = isRecord(screen.content) ? screen.content : {};
  const options = isRecord(content.options) ? content.options : {};
  const items = Array.isArray(options.items) ? options.items : [];
  for (const item of items) {
    if (!isRecord(item)) continue;
    if (typeof item.variableValue === 'string' && item.variableValue) values.add(item.variableValue);
    if (typeof item.id === 'string' && item.id) values.add(item.id);
    const title = resolveDisplayText(item.title);
    if (title) values.add(title);
  }
  return values;
}

export function flattenStrategyToFlowDocument(input: StrategyFlattenInput): StrategyFlattenResult {
  const warnings: string[] = [];
  const bundles = input.blocks.map((item) => normalizeBundle(item));
  let prepared = bundles.map((bundle, index) => prepareBlock(bundle.block, bundle.screens, index, input.strategyId));

  // C9-S4: transitive reachability from the launch block over block-level
  // edges — disconnected clone/variant blocks (and chains hanging off them)
  // compile as dead weight plus a reachability warning per screen otherwise.
  let excludedBlockEntityIds: string[] = [];
  if (input.excludeUnreachable === true && prepared.length > 0) {
    const launch = prepared.find((block) => block.block.isLaunchBlock || block.block.isFirstBlock) ?? prepared[0];
    const reachable = new Set<string>([launch.blockEntityId]);
    const queue = [launch];
    const byEntity = new Map(prepared.map((block) => [block.blockEntityId, block]));
    while (queue.length > 0) {
      const current = queue.shift()!;
      const action = normalizeBlockActionToStrategySchema(current.block.action);
      for (const edge of action.edges ?? []) {
        const target = getBlockEdgeTarget(edge);
        if (!target || reachable.has(target)) continue;
        const targetBlock = byEntity.get(target);
        if (!targetBlock) continue;
        reachable.add(target);
        queue.push(targetBlock);
      }
    }
    excludedBlockEntityIds = prepared
      .filter((block) => !reachable.has(block.blockEntityId))
      .map((block) => block.blockEntityId);
    if (excludedBlockEntityIds.length > 0) {
      prepared = prepared.filter((block) => reachable.has(block.blockEntityId));
      warnings.push(
        `excludeUnreachable: ${excludedBlockEntityIds.length} block(s) not reachable from the launch block `
        + `were excluded from the compile: ${excludedBlockEntityIds.join(', ')}. They stay on the strategy; `
        + 'a scoped blocks push re-includes one.',
      );
    }
  }

  const preparedByEntity = new Map(prepared.map((block) => [block.blockEntityId, block]));
  const flowScreens: Record<string, StrategyFlowScreen> = {};

  // B1-W2: extent-aware block placement replaces the legacy x*4/y*3 origin
  // shift — each block's screens translate by a packed, collision-free
  // compiledOrigin persisted per block in the manifest for the exact inverse.
  const compiledPlacements = computeCompiledBlockPlacements(prepared.map((block, index) => ({
    blockEntityId: block.blockEntityId,
    origin: resolveBlockOrigin(block.block, index),
    screenPositions: block.screens
      .map((screen) => screen.position)
      .filter(isPosition),
    order: index,
  })));

  for (const block of prepared) {
    const compiledOrigin = compiledPlacements.get(block.blockEntityId) ?? { x: 0, y: 0 };

    for (const screen of block.screens) {
      const localId = getScreenId(screen);
      if (!localId) continue;

      const globalId = block.localToGlobal.get(localId);
      if (!globalId) continue;

      const cloned = cloneJson(screen);
      cloned.id = globalId;
      delete cloned.entityId;
      delete cloned.aiContent;
      cloned.tags = mergeTags(cloned.tags, [
        `${STRATEGY_TAG_PREFIX}${input.strategyId}`,
        `${STRATEGY_BLOCK_TAG_PREFIX}${block.blockEntityId}`,
        ...(block.blockId ? [`${STRATEGY_BLOCK_DOC_TAG_PREFIX}${block.blockId}`] : []),
      ]);
      cloned.aiMeta = {
        ...(isRecord(cloned.aiMeta) ? cloned.aiMeta : {}),
        strategyId: input.strategyId,
        blockEntityId: block.blockEntityId,
        // Same guard as the enrich path above: snapshots without an inline
        // block id must not write `blockDocId: undefined` into screensGraph
        // (Firestore rejects the version doc with "invalid nested entity").
        ...(block.blockId ? { blockDocId: block.blockId } : {}),
        localScreenId: localId,
        globalScreenId: globalId,
        screenRole: inferScreenRole(localId, block.entryLocalId, block.exitLocalId),
      };

      if (isRecord(cloned.content)) {
        remapTriggerTargetIdsInPlace(cloned.content, block.localToGlobal, {
          dropUnmapped: false,
          remapConditionKeys: true,
        });
      }

      if (isPosition(cloned.position)) {
        cloned.position = {
          x: compiledOrigin.x + cloned.position.x,
          y: compiledOrigin.y + cloned.position.y,
        };
      }

      flowScreens[globalId] = cloned;
    }
  }

  for (const block of prepared) {
    const action = normalizeBlockActionToStrategySchema(block.block.action);
    const boundaryEdges = (action.edges ?? []).filter((edge) => getBlockEdgeTarget(edge));

    // B1-W4 guarded repair for OLD persisted strategies: a block-level fork
    // whose every edge is conditional strands the runtime when nothing
    // matches. Blocks with any hand-crafted per-exit edge (sourceScreenId)
    // are left untouched. Freshly normalized strategies already carry the
    // default from normalizeStrategyBlockGraph — the unique-append below
    // keeps this idempotent.
    const edgesToCompile: BlockEdgeLike[] = [...boundaryEdges];
    const isBlockLevelFork = boundaryEdges.length > 0
      && boundaryEdges.every((edge) => !edge.sourceScreenId)
      && boundaryEdges.every((edge) => readEdgeConditions(edge).length > 0);
    if (isBlockLevelFork) {
      const template = boundaryEdges[0];
      edgesToCompile.push({
        nextBlockId: getBlockEdgeTarget(template),
        transitionKind: template.transitionKind,
        conditions: [],
      });
      warnings.push(
        `Block ${block.blockEntityId}: conditional-only fork gained a synthetic unconditional default edge to ${getBlockEdgeTarget(template)} (B1-W4).`,
      );
    }

    for (const edge of edgesToCompile) {
      const targetBlockId = getBlockEdgeTarget(edge);
      if (!targetBlockId) continue;

      const targetBlock = preparedByEntity.get(targetBlockId);
      const targetGlobalId = resolveTargetGlobalId(targetBlock, edge);
      if (!targetBlock || !targetGlobalId) {
        warnings.push(`Block ${block.blockEntityId} references missing target block ${targetBlockId}.`);
        continue;
      }

      // B1-W5: edges without an explicit source anchor to EVERY exit screen
      // (replication) — sibling exit variants all continue to the next block.
      for (const sourceGlobalId of resolveSourceGlobalIds(block, edge)) {
        const sourceScreen = flowScreens[sourceGlobalId];
        if (!sourceScreen || !isRecord(sourceScreen.content)) continue;

        // D-T9a-5: the carrier is NEVER a retreat (header back) trigger — a
        // boundary edge on a back button dead-ends the route. When the exit
        // screen offers no advancing trigger at all the walker synthesizes an
        // explicit primary action; that is announced, never silent.
        const outcome = appendTriggerEdgeAtActionKeyInPlace(sourceScreen.content, edge.sourceActionKey, {
          nextScreenId: targetGlobalId,
          transitionKind: edge.transitionKind || 'push',
          conditions: remapConditionKeysInArray(readEdgeConditions(edge), block.localToGlobal),
        }, { unique: true });
        if (outcome === 'synthesized-primary') {
          warnings.push(
            `Block ${block.blockEntityId}: exit screen ${sourceGlobalId} had no advancing trigger `
            + `(only a back/retreat action) — synthesized an actionBar.primary carrier for the `
            + `boundary edge to ${targetGlobalId} (D-T9a-5); give the screen a real primary CTA.`,
          );
        }
        // D-W6-4: the edge WAS appended but it sits behind an unconditional
        // sibling on the same trigger, so the engine can never take it — every
        // screen behind the boundary is unreachable. Never silent: on W6 this
        // exact shape cost the fasting route its paywall while the compile
        // reported outcome `preferred` and the healthcheck read 84/84.
        if (outcome === 'shadowed') {
          const carrierActionKey = collectShadowedTriggerEdges(sourceScreen.content)
            .find((dead) => dead.nextScreenId === targetGlobalId)?.actionKey
            ?? edge.sourceActionKey
            ?? 'actionBar.primary.action';
          warnings.push(
            `Block ${block.blockEntityId}: boundary edge to ${targetGlobalId} was appended to `
            + `${sourceGlobalId} ${carrierActionKey} BEHIND an existing unconditional edge and is `
            + 'SHADOWED — first-match-wins means it can never be taken and everything behind the boundary is '
            + 'unreachable (D-W6-4); the exit screen needs a terminal trigger, or the existing edge '
            + 'needs a condition.',
          );
        }
      }
    }
  }

  // Defect #4b: terminal blocks (action kind `complete`, no boundary edges)
  // previously got NO screen-level trigger at all — their exit screens
  // compiled as dead-ends. Stamp an explicit terminal `complete` action so
  // the engine finishes the funnel instead of stranding the user; screens
  // that already navigate are never touched.
  for (const block of prepared) {
    const action = normalizeBlockActionToStrategySchema(block.block.action);
    if (action.kind !== 'complete' || (action.edges ?? []).length > 0) continue;

    // B1-W5: every exit variant of a terminal block gets the terminal stamp,
    // not just the single legacy exit pick.
    const exitGlobalIds = block.exitGlobalIds.length > 0
      ? block.exitGlobalIds
      : block.exitGlobalId ? [block.exitGlobalId] : [];
    for (const exitGlobalId of exitGlobalIds) {
      const exitScreen = flowScreens[exitGlobalId];
      if (!exitScreen || !isRecord(exitScreen.content)) continue;

      const outcome = ensureTerminalActionInPlace(exitScreen.content);
      if (outcome === 'stamped') {
        warnings.push(
          `Block ${block.blockEntityId}: exit screen ${exitGlobalId} had no navigable trigger — stamped terminal complete action (defect #4b).`,
        );
      }
    }
  }

  // D-T9b-6, the half compile still owns (R-W70-35). Runs AFTER the terminal
  // stamp above, because that stamp is what can land on a Paywall's purchase
  // trigger and flip it to `complete` — the screen139 shape. A purchase with no
  // success destination is reported, never repaired with an invented screen.
  warnings.push(...materializePurchaseActionIntegrity(flowScreens));

  const launchBlock = prepared.find((block) => block.block.isLaunchBlock || block.block.isFirstBlock) ?? prepared[0];
  const launchScreenId = launchBlock?.entryGlobalId ?? Object.keys(flowScreens)[0] ?? '';
  const manifest: StrategyFlowManifest = {
    version: 1,
    layoutVersion: COMPILED_LAYOUT_VERSION,
    strategyId: input.strategyId,
    strategyName: input.strategyName,
    generatedAt: new Date().toISOString(),
    blocks: prepared.map((block, index) => ({
      blockId: block.blockId,
      blockEntityId: block.blockEntityId,
      name: block.block.name,
      blockType: block.block.blockType,
      customSubtype: block.block.customSubtype,
      instructions: block.block.instructions,
      order: block.block.order,
      taskId: block.block.taskId ?? null,
      isFirstBlock: block.block.isFirstBlock,
      isLaunchBlock: block.block.isLaunchBlock,
      firstScreenId: block.block.firstScreenId,
      entryScreenId: block.entryLocalId,
      exitScreenId: block.exitLocalId,
      screenIdMap: Object.fromEntries(block.localToGlobal),
      compileHints: compactCompileHints({
        ...(block.block.compileHints ?? {}),
        entryScreenId: block.entryLocalId,
        exitScreenId: block.exitLocalId,
      }),
      blockData: {
        ...stripLargeBlockFields(block.block),
        // The RESOLVED block-grid origin (explicit block position or the
        // index fallback) — restores the block's strategy-canvas position on
        // lift even for blocks that never had one.
        position: resolveBlockOrigin(block.block, index),
        // The EXACT translation this compile applied to the block's screens
        // (B1-W2) — the lift subtracts it verbatim for a byte-exact inverse.
        compiledOrigin: compiledPlacements.get(block.blockEntityId) ?? { x: 0, y: 0 },
      },
    })),
    ...(excludedBlockEntityIds.length > 0 ? { excludedBlockEntityIds } : {}),
  };

  const metadata: Record<string, string> = stringifyMetadata(input.metadata);
  metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY] = JSON.stringify(manifest);

  const omittedHeavyBlockCount = manifest.blocks
    .filter((entry) => Array.isArray((entry.blockData as Record<string, unknown> | undefined)?.[MANIFEST_OMITTED_HEAVY_KEYS_FIELD]))
    .length;
  if (omittedHeavyBlockCount > 0) {
    warnings.push(
      `Manifest blockData omitted heavy generation-context keys (${MANIFEST_HEAVY_BLOCK_DATA_KEYS.join(', ')}) `
      + `for ${omittedHeavyBlockCount} block(s) to fit the version-document ceiling; the strategy block docs `
      + 'keep the values and an absorb-lift carries them over.',
    );
  }

  // Defect #2: remapped strategy variables win; synthesis fills only variable
  // ids that are absent or left without a single resolvable binding. Every
  // synthesized entry is announced — the compile output stays auditable.
  const remapped = remapVariableScreenRefs(input.variables, prepared);
  warnings.push(...remapped.warnings);
  const synthesized = synthesizeVariablesFromRealizations(flowScreens, input.defaultLanguage ?? 'en-US');
  const variables = remapped.variables;
  const filledVariableIds = new Set<string>();
  for (const [variableId, entry] of Object.entries(synthesized.variables)) {
    const existing = variables[variableId];
    if (existing === undefined) {
      variables[variableId] = entry;
      filledVariableIds.add(variableId);
      warnings.push(`Variable ${variableId}: synthesized from aiMeta.variableRealizations (absent from strategy variables — defect #2).`);
    } else if (!hasResolvableBindings(existing)) {
      variables[variableId] = {
        ...(existing as Record<string, unknown>),
        id: (existing as Record<string, unknown>).id ?? entry.id,
        name: (existing as Record<string, unknown>).name ?? entry.name,
        type: (existing as Record<string, unknown>).type ?? entry.type,
        required: (existing as Record<string, unknown>).required ?? entry.required,
        scope: (existing as Record<string, unknown>).scope ?? entry.scope,
        ...((existing as Record<string, unknown>).options !== undefined || entry.options !== undefined
          ? { options: (existing as Record<string, unknown>).options ?? entry.options }
          : {}),
        boundScreenIds: entry.boundScreenIds,
        ...(entry.screenBindings !== undefined ? { screenBindings: entry.screenBindings } : {}),
      };
      filledVariableIds.add(variableId);
      warnings.push(`Variable ${variableId}: bindings synthesized from aiMeta.variableRealizations (none resolvable after remap — defect #2).`);
    }
  }
  // Screen-level synthesis notes surface only for variables that actually
  // needed synthesis or stayed unbound — quiet on healthy compiles.
  for (const note of synthesized.warnings) {
    const relevant = !note.variableId
      || filledVariableIds.has(note.variableId)
      || !hasResolvableBindings(variables[note.variableId]);
    if (relevant) warnings.push(`variables synthesis: ${note.message}`);
  }

  // B1-W4 safety net: a goals fork routes on the task-router variable but no
  // declared+bound variable backs it — or its bindings are STALE (binding
  // values no longer among the bound screen's option values, e.g. the save
  // path renamed option variableValues after the bindings were written; the
  // runtime reverse-lookup would silently fall to the default edge on every
  // pick). Synthesize/rebuild from the fork block's goal-selection screen
  // with GLOBAL screen ids. The generation executor's write is the primary
  // path; an operator-declared variable with RESOLVABLE bindings always wins.
  const existingRouter = variables[TASK_ROUTER_VARIABLE_ID];
  // Task names from the fork targets' block names keep the option→task
  // mapping semantic (taskIds themselves are opaque Firestore ids).
  const taskNamesById: Record<string, string> = {};
  for (const block of prepared) {
    const taskId = (block.block as Record<string, unknown>).taskId;
    const name = block.block.name;
    if (typeof taskId === 'string' && taskId && typeof name === 'string' && name && !taskNamesById[taskId]) {
      taskNamesById[taskId] = name;
    }
  }
  const router = buildTaskRouterVariableForBlocks(
    prepared.map((block) => ({ ...block.block, entityId: block.blockEntityId, screens: block.screens })),
    {
      taskNamesById,
      screenIdFor: (localScreenId) => {
        const owner = prepared.find((block) => block.localToGlobal.has(localScreenId));
        return owner?.localToGlobal.get(localScreenId);
      },
    },
  );
  const expectedTaskIds = [
    ...router.assignments.map((assignment) => assignment.taskId),
    ...router.unmappedTaskIds,
  ];
  if (!routerBindingsResolvable(existingRouter, flowScreens, expectedTaskIds)) {
    const completeRouter = router.variable
      && isRecord(router.variable.screenBindings)
      && router.unmappedTaskIds.length === 0;
    if (completeRouter && router.variable) {
      const routerVariable = router.variable;
      variables[TASK_ROUTER_VARIABLE_ID] = existingRouter === undefined
        ? routerVariable
        : {
          ...(existingRouter as Record<string, unknown>),
          options: (existingRouter as Record<string, unknown>).options ?? routerVariable.options,
          boundScreenIds: routerVariable.boundScreenIds,
          screenBindings: routerVariable.screenBindings,
        };
      warnings.push(
        `Variable ${TASK_ROUTER_VARIABLE_ID}: synthesized router bindings from the goals fork (B1-W4)`
        + ` — all ${router.assignments.length} task(s) bound.`,
      );
      for (const note of router.warnings) warnings.push(`task router synthesis: ${note}`);
    } else if (expectedTaskIds.length > 0) {
      // A declared-but-partial router makes every condition look structurally
      // valid while unmatched goal options silently take the default edge.
      // Remove it from the COMPILED artifact so strict health emits the real
      // unknown-variable findings and B3 can route them into an instructed
      // retry. Raw strategy variables remain untouched.
      delete variables[TASK_ROUTER_VARIABLE_ID];
      warnings.push(
        `Variable ${TASK_ROUTER_VARIABLE_ID}: router synthesis refused incomplete coverage`
        + ` — ${router.assignments.length}/${expectedTaskIds.length} task(s) mapped; compile left it undeclared for repair.`,
      );
      for (const note of router.warnings) warnings.push(`task router synthesis: ${note}`);
    }
  }

  // D-T9a-8: a REQUIRED value input must be impossible to advance past empty.
  // The V2 TextFieldConfig schema carries no `required` flag, so the ONLY
  // runtime refusal the engine understands is the primary CTA visibility gate
  // `disabledUntilValueEntered` (screen-view-v2/utils/buttonVisibility.ts).
  // Compile is the layer that holds BOTH the variable definitions and the
  // screens collecting them, so it owns the translation; an explicit author
  // choice (`hiddenUntilValueEntered`) is never overridden.
  warnings.push(...stampRequiredInputGates(flowScreens, variables));

  // W2 materializations (compiledScreenMaterializers.ts) — same ownership
  // argument as the D-T9a-8 gate above: compile is the only layer holding both
  // the declarations and the screens realizing them.
  const materializeLocale = input.defaultLanguage ?? 'en-US';
  // (a) D-T9b-1 — the price slot is paywall-variable text, never a literal.
  warnings.push(...materializePaywallPriceLabels(flowScreens, materializeLocale));
  // (c)/(d) — declared value shape reaches the field that collects it.
  warnings.push(...materializeInputConstraints(flowScreens, variables, materializeLocale));
  // W7 (f) / R-W70-16 — an option subtitle under a title-only layout is copy
  // the reader never sees; promote the layout, or say why it could not be.
  warnings.push(...materializeItemLayoutSubtitles(flowScreens));
  // W7 (d) — a custom screen that paints no ground of its own gets the theme
  // default consuming the tokens the runtime exposes (UX M-3, compile half).
  warnings.push(...materializeEmbedThemeDefaults(flowScreens));
  // The "More options" disclosure's words are NOT compile's to write
  // (controller ruling R-W70-38, extending the user's R-W70-35 from screens to
  // COPY). `content.options.moreOptionsLabel` / `fewerOptionsLabel` come from
  // generation or from the owner in the editor; a document that carries
  // neither shows every option and no disclosure. A seed in the funnel's
  // default language was still the platform putting words in front of a
  // visitor, and a default nobody chose is the hardest kind to notice.
  //
  // Neither is the header BACK affordance nor the option SHUFFLE compile's to
  // decide (owner ruling R-POST-3, W11 items 4 and 5, extending R-W70-38 from
  // copy to affordances and to the order the visitor reads). W2's
  // `materializeBackAffordance` seeded a `header.back` retreat trigger labelled
  // "Back" on every pick and TextInput screen, and W8's
  // `materializeOptionRandomization` set `isRandomized: true` on every
  // unordered list of three or more options — a control and an ordering the
  // owner never asked for, invisible in the editor. Both are gone: an AUTHORED
  // `header.back` and an EXPLICIT `isRandomized` are transported unchanged, an
  // absent one stays absent, and the health report warns
  // (`graph.noRetreatAffordance`). The opt-in carrier is the owner's to define.

  return {
    flowDocument: {
      schemaVersion: '2.0.0',
      defaultLanguage: input.defaultLanguage ?? 'en-US',
      languages: input.languages?.length ? input.languages : [input.defaultLanguage ?? 'en-US'],
      launchScreenId,
      screens: flowScreens as unknown as FlowDocument['screens'],
      metadata,
      variables,
    } as FlowDocument & { metadata: Record<string, string>; variables?: Record<string, unknown> },
    manifest,
    warnings,
    filledVariableIds: Array.from(filledVariableIds),
  };
}

export function liftFlowDocumentToStrategy(input: StrategyLiftInput): StrategyLiftResult {
  const flowDocument = input.flowDocument;
  const screens = isRecord(flowDocument.screens) ? flowDocument.screens as Record<string, StrategyFlowScreen> : {};
  const manifest = parseManifest(flowDocument.metadata);
  const warnings: string[] = [];
  const blocks: StrategyFlowBlock[] = [];

  const foreignTagIgnored: StrategyLiftForeignTagIgnored[] = [];
  const manifestBlocks = resolveLiftManifestBlocks(
    screens,
    manifest,
    input.strategyId ?? 'strategy',
    typeof flowDocument.launchScreenId === 'string' ? flowDocument.launchScreenId : undefined,
    input.strategyId,
    warnings,
    foreignTagIgnored,
  );
  const screenToBlock = buildScreenToBlockIndexFromItems(manifestBlocks);
  const globalToLocalIndex = buildGlobalToLocalIndex(manifestBlocks);

  for (let blockIndex = 0; blockIndex < manifestBlocks.length; blockIndex++) {
    const item = manifestBlocks[blockIndex];
    const globalIds = Object.values(item.screenIdMap).filter((id) => Boolean(screens[id]));
    if (globalIds.length === 0) {
      warnings.push(`Block ${item.blockEntityId} has no screens in FlowDocument.`);
      continue;
    }

    const globalToLocal = new Map<string, string>();
    for (const [localId, globalId] of Object.entries(item.screenIdMap)) {
      if (screens[globalId]) {
        globalToLocal.set(globalId, localId);
      }
    }

    // Spec D4 (serializer hygiene): invert the flatten's position shift so
    // screen positions round-trip byte-exact. Precedence (B1-W2):
    // 1. blockData.compiledOrigin — the EXACT translation the extent-aware
    //    placement applied (layoutVersion 2 compiles): subtract verbatim.
    // 2. blockData.position — legacy compiles shifted by origin x*4 / y*3.
    // 3. neither — oldest manifests keep flow positions verbatim.
    const blockData = isRecord(item.blockData) ? item.blockData as Record<string, unknown> : undefined;
    const compiledOrigin = blockData && isPosition(blockData.compiledOrigin)
      ? blockData.compiledOrigin
      : undefined;
    const exactBlockOrigin = !compiledOrigin && blockData && isPosition(blockData.position)
      ? blockData.position as { x: number; y: number }
      : undefined;

    const outgoingTargets: BlockBoundaryTarget[] = [];
    const localScreens = globalIds.map((globalId, screenIndex) => {
      const original = screens[globalId];
      const localId = globalToLocal.get(globalId) ?? stripGlobalScreenId(globalId, item.blockEntityId) ?? `screen_${screenIndex + 1}`;
      const cloned = cloneJson(original);
      cloned.id = localId;
      delete cloned.entityId;
      if (compiledOrigin && isPosition(cloned.position)) {
        cloned.position = {
          x: cloned.position.x - compiledOrigin.x,
          y: cloned.position.y - compiledOrigin.y,
        };
      } else if (exactBlockOrigin && isPosition(cloned.position)) {
        cloned.position = {
          x: cloned.position.x - exactBlockOrigin.x * 4,
          y: cloned.position.y - exactBlockOrigin.y * 3,
        };
      }
      cloned.aiMeta = {
        ...(isRecord(cloned.aiMeta) ? cloned.aiMeta : {}),
        localScreenId: localId,
        globalScreenId: globalId,
        blockEntityId: item.blockEntityId,
      };

      if (isRecord(cloned.content)) {
        rewriteTriggerEdgesInPlace(cloned.content, (edge: TriggerEdgeLike, context) => {
          const target = edge.nextScreenId;
          if (!target) return edge;

          const sameBlockLocalTarget = globalToLocal.get(target);
          if (sameBlockLocalTarget) {
            // Absence-preserving: never mint `conditions: []` on an edge that
            // had no conditions key (Spec D4 serializer hygiene).
            const remappedConditions = remapConditionKeysInArray(readEdgeConditions(edge), globalToLocal);
            const rewritten: TriggerEdgeLike = { ...edge, nextScreenId: sameBlockLocalTarget };
            if (remappedConditions.length > 0 || Array.isArray(edge.conditions)) {
              rewritten.conditions = remappedConditions as TriggerEdgeLike['conditions'];
            }
            return rewritten;
          }

          const targetBlockId = screenToBlock.get(target);
          if (targetBlockId && targetBlockId !== item.blockEntityId) {
            const targetScreenId = globalToLocalIndex.get(target) ?? stripGlobalScreenId(target, targetBlockId) ?? target;
            outgoingTargets.push({
              targetEntityId: targetBlockId,
              sourceScreenId: localId,
              sourceActionKey: context.actionKey,
              targetScreenId,
              transitionKind: edge.transitionKind,
              conditions: remapConditionKeysInArray(readEdgeConditions(edge), globalToLocal),
            });
            return null;
          }

          warnings.push(`Dropped unresolved cross-screen edge ${globalId} -> ${target}.`);
          return null;
        });
      }

      return cloned;
    });

    const isLaunchBlock = globalIds.includes(String(flowDocument.launchScreenId || ''));
    const blockBase = isRecord(item.blockData) ? cloneJson(item.blockData) : {};
    // compiledOrigin is compile-plumbing (the applied screen translation) —
    // it must not leak onto the lifted strategy block document.
    delete (blockBase as Record<string, unknown>).compiledOrigin;
    const firstScreenId = item.entryScreenId ?? globalToLocal.get(String(flowDocument.launchScreenId || '')) ?? localScreens[0]?.id ?? '';

    blocks.push({
      ...blockBase,
      id: item.blockId,
      entityId: item.blockEntityId,
      name: item.name ?? String(blockBase.name || `Block ${blockIndex + 1}`),
      blockType: item.blockType ?? String(blockBase.blockType || 'custom'),
      customSubtype: item.customSubtype ?? (blockBase.customSubtype as string | undefined),
      instructions: item.instructions ?? (blockBase.instructions as StrategyBlockInstruction[] | undefined),
      order: item.order ?? blockIndex,
      taskId: item.taskId ?? (blockBase.taskId as string | null | undefined) ?? null,
      firstScreenId,
      position: deriveLiftedBlockPosition(blockBase, globalIds, screens, blockIndex),
      isFirstBlock: isLaunchBlock || item.isFirstBlock || blockIndex === 0,
      isLaunchBlock: isLaunchBlock || item.isLaunchBlock || blockIndex === 0,
      action: createStrategySchemaBlockAction(dedupeBlockTargets(outgoingTargets), outgoingTargets.length > 0 ? 'advance' : 'complete'),
      compileHints: compactCompileHints({
        ...(item.compileHints ?? {}),
        entryScreenId: firstScreenId,
        exitScreenId: item.exitScreenId,
        // Stale-id filter (B1-W5): keep only exit hints that still resolve to
        // a screen in this block after the lift.
        exits: filterExitHintsToScreens(item.compileHints?.exits, localScreens),
      }),
      screens: localScreens,
    });
  }

  ensureLiftedBlockPositions(blocks);

  const looseBlock = blocks.find((block) => getBlockEntityId(block) === '_untagged');
  const blockSuggestions = looseBlock
    ? buildLooseScreenBlockSuggestions(screens, screenToBlock, manifestBlocks)
    : new Map<string, string[]>();
  const looseScreens: StrategyLiftLooseScreen[] = (looseBlock?.screens ?? []).map((screen) => {
    const globalId = String(screen.aiMeta?.globalScreenId || screen.id || '');
    const suggested = blockSuggestions.get(globalId);
    return {
      screenId: globalId,
      screenName: typeof screen.name === 'string' ? screen.name : undefined,
      screenType: typeof screen.screenType === 'string' ? screen.screenType : undefined,
      ...(suggested && suggested.length > 0 ? { suggestedBlockEntityIds: suggested } : {}),
    };
  });

  return {
    blocks,
    manifest,
    warnings,
    looseScreens: looseScreens.length > 0 ? looseScreens : undefined,
    foreignTagIgnored: foreignTagIgnored.length > 0 ? foreignTagIgnored : undefined,
  };
}

/**
 * Ranks candidate blocks for each loose (`_untagged`) screen by counting trigger
 * edges between the loose screen and screens with block membership, in both
 * directions. Works on the ORIGINAL FlowDocument screens (targets are global ids).
 */
function buildLooseScreenBlockSuggestions(
  screens: Record<string, StrategyFlowScreen>,
  screenToBlock: Map<string, string>,
  manifestBlocks: StrategyBlockManifestItem[],
): Map<string, string[]> {
  const blockRank = new Map<string, number>();
  manifestBlocks.forEach((item, index) => {
    if (item.blockEntityId !== '_untagged') {
      blockRank.set(item.blockEntityId, index);
    }
  });
  if (blockRank.size === 0) return new Map();

  const edgeCounts = new Map<string, Map<string, number>>();
  const bump = (looseGlobalId: string, blockEntityId: string) => {
    if (!blockRank.has(blockEntityId)) return;
    const perBlock = edgeCounts.get(looseGlobalId) ?? new Map<string, number>();
    perBlock.set(blockEntityId, (perBlock.get(blockEntityId) ?? 0) + 1);
    edgeCounts.set(looseGlobalId, perBlock);
  };

  for (const [globalId, screen] of Object.entries(screens)) {
    if (!isRecord(screen?.content)) continue;
    const sourceBlock = screenToBlock.get(globalId);
    for (const edge of collectTriggerEdges(screen.content)) {
      const target = typeof edge.nextScreenId === 'string' ? edge.nextScreenId : undefined;
      if (!target) continue;
      const targetBlock = screenToBlock.get(target);
      if (sourceBlock === '_untagged' && targetBlock && targetBlock !== '_untagged') {
        bump(globalId, targetBlock);
      } else if (targetBlock === '_untagged' && sourceBlock && sourceBlock !== '_untagged') {
        bump(target, sourceBlock);
      }
    }
  }

  const suggestions = new Map<string, string[]>();
  for (const [looseGlobalId, perBlock] of edgeCounts) {
    const ranked = [...perBlock.entries()]
      .sort((a, b) => (b[1] - a[1]) || ((blockRank.get(a[0]) ?? 0) - (blockRank.get(b[0]) ?? 0)))
      .slice(0, 3)
      .map(([blockEntityId]) => blockEntityId);
    if (ranked.length > 0) {
      suggestions.set(looseGlobalId, ranked);
    }
  }
  return suggestions;
}

export type StrategyPushChangeKind = 'copy' | 'edge' | 'asset';

export interface StrategyPushResolutions {
  /**
   * Per-screen conflict resolution keyed by GLOBAL screen id. Applies only to
   * conflict rows (both sides diverged from the aiContent base).
   * Default: 'funnel' — the onboarding-side polish wins.
   */
  screens?: Record<string, 'funnel' | 'strategy'>;
  /** Per-variable resolution keyed by variable id. */
  variables?: Record<string, VariableResolutionAction>;
  /** Per-slot asset resolution: screenId (global) -> slotKey -> resolution. */
  assetSlots?: Record<string, Record<string, AiAssetSlotResolution>>;
}

export interface StrategyPushPlanInput {
  strategyId: string;
  /** Result of flattenStrategyToFlowDocument over the full strategy. */
  flattened: StrategyFlattenResult;
  /** The SAME blocks input given to flatten — block-local screens are the diff base. */
  blocks: Array<StrategyFlowBlock | StrategyBlockScreenBundle>;
  /** Current screensV2 of the target onboarding version, keyed by global screen id. */
  existingScreens: Record<string, StrategyFlowScreen>;
  /** Current version screensGraph.metadata (may hold a manifest and association fields). */
  existingMetadata?: Record<string, unknown>;
  /** Limit the push to these block entity ids; omitted = whole strategy. */
  blockEntityIds?: string[];
  /** Simplified projection of a screen's content; null when the screen cannot be simplified. */
  toSimplifiedContent: (screen: StrategyFlowScreen) => Record<string, unknown> | null;
  /**
   * Variables currently on the target version. When provided, the plan includes
   * a variable diff and mergedVariables computed via per-variable resolutions.
   */
  existingVariables?: Record<string, unknown>;
  /** User decisions from the confirm dialog / --resolutions-file. */
  resolutions?: StrategyPushResolutions;
  /** Locale for asset mirror ops; defaults to the flattened defaultLanguage. */
  locale?: string;
}

export interface StrategyPushScreenPlan {
  screenId: string;
  blockEntityId: string;
  action: 'create' | 'replace' | 'preserve' | 'delete';
  /** preserve only: strategy-managed cross-block edges were rewritten. */
  edgesUpdated?: boolean;
  /** The onboarding side had also diverged from the aiContent base. */
  conflict?: boolean;
  /** Display name of the screen (strategy side if present, else onboarding). */
  screenName?: string;
  /** What kind of content differs between the two sides. */
  changeKinds?: StrategyPushChangeKind[];
  /** Why a replace happened without a clean three-way diff. */
  reason?: 'no-baseline' | 'not-simplifiable';
  /** conflict rows: which side won under the applied resolution. */
  resolvedWith?: 'funnel' | 'strategy';
  /** Per-slot AI asset diff for this screen (only when slots exist on either side). */
  assetSlots?: AiAssetSlotDiffEntry[];
}

export interface StrategyPushPlanResult {
  /** Documents to set, keyed by global screen id (create/replace and edge-patched preserves). */
  writes: Record<string, StrategyFlowScreen>;
  deletes: string[];
  plan: StrategyPushScreenPlan[];
  /** Merged version metadata including the updated manifest (values stringified). */
  metadata: Record<string, string>;
  /** Set only when the launch block is in scope. */
  launchScreenId?: string;
  warnings: string[];
  /** Present when existingVariables was provided. */
  variableDiff?: VariableDiffResult;
  /** Full merged variable map to write to the target version. */
  mergedVariables?: Record<string, Record<string, unknown>>;
  /** Forked variables to write back to the strategy for id consistency. */
  forkedVariables?: VariableForkRecord[];
  /** Flat per-slot asset diff across all in-scope screens. */
  assetDiff?: AiAssetSlotDiffEntry[];
}

/**
 * Plans an in-place push of strategy content into an existing compiled onboarding version.
 * A screen is overwritten only when its strategy-side simplified content differs from the
 * screen's stored aiContent snapshot, so canvas-side UI polish on unchanged screens survives.
 */
export function planStrategyPushIntoFlowDocument(input: StrategyPushPlanInput): StrategyPushPlanResult {
  const warnings: string[] = [];
  const writes: Record<string, StrategyFlowScreen> = {};
  const deletes: string[] = [];
  const plan: StrategyPushScreenPlan[] = [];

  const manifestBlocks = input.flattened.manifest.blocks;
  // C9-S4: blocks excluded at compile stay out of the default push scope — the
  // first push after an --exclude-unreachable compile must not would_create 20
  // dead screens. An explicitly scoped push re-includes (and un-excludes) one.
  const compiledExcludedIds = new Set(parseManifest(input.existingMetadata)?.excludedBlockEntityIds ?? []);
  const scope = input.blockEntityIds?.length
    ? input.blockEntityIds
    : manifestBlocks.map((item) => item.blockEntityId).filter((id) => !compiledExcludedIds.has(id));
  const scopeSet = new Set(scope);
  if (!input.blockEntityIds?.length && compiledExcludedIds.size > 0) {
    const skipped = manifestBlocks
      .map((item) => item.blockEntityId)
      .filter((id) => compiledExcludedIds.has(id));
    if (skipped.length > 0) {
      warnings.push(
        `Push skipped ${skipped.length} block(s) excluded at compile (excludeUnreachable): ${skipped.join(', ')}. `
        + 'Push one explicitly (strategies blocks push) to re-include it.',
      );
    }
  }

  const strategyGlobalIndex = buildScreenToBlockIndexFromItems(manifestBlocks);
  const freshScreens = input.flattened.flowDocument.screens as unknown as Record<string, StrategyFlowScreen>;
  const inScopeFreshIds = new Set<string>();
  for (const item of manifestBlocks) {
    if (!scopeSet.has(item.blockEntityId)) continue;
    for (const globalId of Object.values(item.screenIdMap)) {
      if (freshScreens[globalId]) inScopeFreshIds.add(globalId);
    }
  }

  const localScreensByBlock = new Map<string, Map<string, StrategyFlowScreen>>();
  for (const bundle of input.blocks.map((item) => normalizeBundle(item))) {
    const blockEntityId = getBlockEntityId(bundle.block) || '';
    const byLocalId = new Map<string, StrategyFlowScreen>();
    for (const screen of bundle.screens ?? []) {
      const localId = getScreenId(screen);
      if (localId) byLocalId.set(localId, screen);
    }
    localScreensByBlock.set(blockEntityId, byLocalId);
  }

  // ── Variable layer: diff + resolutions + ref rewrite for forks ──
  // The aiContent baseline intentionally keeps pre-fork refs (it is computed
  // from the untouched strategy-local screens), so the next push still
  // classifies unchanged screens as preserve.
  const incomingVariables = isRecord(input.flattened.flowDocument.variables)
    ? input.flattened.flowDocument.variables
    : undefined;
  let variableDiff: VariableDiffResult | undefined;
  let mergedVariables: Record<string, Record<string, unknown>> | undefined;
  let forkedVariables: VariableForkRecord[] | undefined;
  if (input.existingVariables !== undefined) {
    const inScopeFreshScreens = Array.from(inScopeFreshIds).map((id) => freshScreens[id]);
    variableDiff = diffFunnelVariables({
      incoming: incomingVariables,
      existing: input.existingVariables,
      incomingScreens: inScopeFreshScreens,
    });
    const applied = applyVariableResolutions({
      incoming: incomingVariables,
      existing: input.existingVariables,
      diff: variableDiff,
      resolutions: input.resolutions?.variables,
    });
    mergedVariables = applied.variables;
    forkedVariables = applied.forked;
    warnings.push(...applied.warnings);

    if (applied.idRewriteMap.size > 0 || applied.refRewriteMap.size > 0) {
      for (const globalId of inScopeFreshIds) {
        const [rewritten] = rewriteVariableRefsInScreens(
          [freshScreens[globalId]],
          applied.idRewriteMap,
          applied.refRewriteMap,
        );
        freshScreens[globalId] = rewritten;
      }
    }
  }

  const locale = input.locale
    ?? String((input.flattened.flowDocument as { defaultLanguage?: string }).defaultLanguage || 'en-US');
  const assetDiff: AiAssetSlotDiffEntry[] = [];

  // Global-id universe for foreign-edge recognition during diff canonicalization:
  // an edge target outside this block's screenIdMap that IS a known global id is
  // a funnel-owned cross-block edge (invisible to the strategy); anything else is
  // assumed block-local and kept.
  const knownGlobalIds = new Set<string>(Object.keys(input.existingScreens));
  for (const item of manifestBlocks) {
    for (const globalId of Object.values(item.screenIdMap)) knownGlobalIds.add(globalId);
  }

  for (const [globalId, existing] of Object.entries(input.existingScreens)) {
    if (inScopeFreshIds.has(globalId)) continue;
    const strategyTag = existing.tags?.find((tag) => tag === `${STRATEGY_TAG_PREFIX}${input.strategyId}`);
    const blockTag = readBlockTag(existing);
    if (strategyTag && blockTag && scopeSet.has(blockTag)) {
      deletes.push(globalId);
      plan.push({
        screenId: globalId,
        blockEntityId: blockTag,
        action: 'delete',
        screenName: typeof existing.name === 'string' ? existing.name : undefined,
      });
    }
  }
  const deleteSet = new Set(deletes);

  for (const item of manifestBlocks) {
    if (!scopeSet.has(item.blockEntityId)) continue;

    const ownGlobalIds = new Set(Object.values(item.screenIdMap));
    const localById = localScreensByBlock.get(item.blockEntityId);
    const globalToLocal = new Map(Object.entries(item.screenIdMap).map(([localId, globalId]) => [globalId, localId]));

    for (const [localId, globalId] of Object.entries(item.screenIdMap)) {
      const fresh = freshScreens[globalId];
      if (!fresh) continue;

      const localScreen = localById?.get(localId);
      const strategySimplified = localScreen ? input.toSimplifiedContent(localScreen) : null;
      const existing = input.existingScreens[globalId];
      const screenName = readScreenName(fresh) ?? readScreenName(existing);

      if (!existing) {
        writes[globalId] = withRefreshedAiContent(fresh, strategySimplified);
        plan.push({ screenId: globalId, blockEntityId: item.blockEntityId, action: 'create', screenName });
        continue;
      }

      // Per-slot AI asset diff runs on every screen pair so experiments never
      // silently disappear — neither on replace (onboarding-only ones) nor on
      // preserve (strategy-only ones).
      const stepKindRaw = fresh.screenType ?? existing.screenType;
      const slotEntries = diffAiAssetSlots({
        screenId: globalId,
        sourceAiAssets: isRecord(fresh.aiAssets) ? fresh.aiAssets as Record<string, unknown> : undefined,
        targetAiAssets: isRecord(existing.aiAssets) ? existing.aiAssets as Record<string, unknown> : undefined,
        stepKind: typeof stepKindRaw === 'string' ? stepKindRaw : undefined,
        locale,
      });
      if (slotEntries.length > 0) assetDiff.push(...slotEntries);
      const assetSlots = slotEntries.length > 0 ? slotEntries : undefined;
      const slotsDiverged = slotEntries.some((entry) => entry.status !== 'both-equal');
      const slotResolutions = input.resolutions?.assetSlots?.[globalId];
      const needsAssetPatch = sourceBringsAssets(slotEntries)
        || Boolean(slotResolutions && Object.keys(slotResolutions).length > 0);

      const base = isRecord(existing.aiContent) ? existing.aiContent : undefined;
      if (!base || !strategySimplified) {
        const reason: 'no-baseline' | 'not-simplifiable' = !base ? 'no-baseline' : 'not-simplifiable';
        warnings.push(
          !base
            ? `Screen ${globalId} has no aiContent snapshot to diff against; overwriting with strategy content.`
            : `Screen ${globalId} could not be simplified for diffing; overwriting with strategy content.`,
        );
        const next = withReplacedScreen(fresh, existing, strategySimplified);
        applyMergedAssetsToWrite(next, fresh, existing, slotResolutions, locale, warnings);
        writes[globalId] = next;
        plan.push({
          screenId: globalId,
          blockEntityId: item.blockEntityId,
          action: 'replace',
          reason,
          screenName,
          changeKinds: slotsDiverged ? ['asset'] : undefined,
          assetSlots,
        });
        continue;
      }

      // C9-S1: every baseline/side comparison runs in the canonical block-local
      // namespace — compile-time baselines carry GLOBAL edge ids, push-side
      // projections carry local ones, and funnel screens carry cross-block edges
      // the strategy cannot represent by construction.
      const canon = (content: Record<string, unknown> | null | undefined) =>
        canonicalizeSimplifiedForDiff(content, globalToLocal, knownGlobalIds);
      const baseCanon = canon(base);
      const strategyCanon = canon(strategySimplified);
      const baselineMatchesStrategy = stableEquals(baseCanon, strategyCanon);

      let existingSimplified: Record<string, unknown> | null = null;
      let existingCanon: Record<string, unknown> | null = null;
      let sidesAgree = false;
      if (!baselineMatchesStrategy) {
        const localizedExisting = cloneJson(existing);
        if (isRecord(localizedExisting.content)) {
          remapTriggerTargetIdsInPlace(localizedExisting.content, globalToLocal, {
            dropUnmapped: false,
            remapConditionKeys: true,
          });
        }
        existingSimplified = input.toSimplifiedContent(localizedExisting);
        existingCanon = canon(existingSimplified);
        // Both sides holding identical content (typical right after a pull) is
        // not a conflict — there is nothing to resolve.
        sidesAgree = Boolean(existingCanon && stableEquals(existingCanon, strategyCanon));
      }

      if (baselineMatchesStrategy || sidesAgree) {
        // Heal byte-stale baselines (global-id or degenerate aiContent) so
        // future pushes take the fast path instead of re-deriving agreement.
        const staleBaseline = !stableEquals(base, strategySimplified);
        const reconciled = reconcileStrategyCrossBlockEdges(
          existing,
          fresh,
          ownGlobalIds,
          strategyGlobalIndex,
          deleteSet,
        );
        if (reconciled || needsAssetPatch || staleBaseline) {
          const patched = reconciled ?? cloneJson(existing);
          if (needsAssetPatch) {
            applyMergedAssetsToWrite(patched, fresh, existing, slotResolutions, locale, warnings);
          }
          if (staleBaseline && strategySimplified) patched.aiContent = cloneJson(strategySimplified);
          writes[globalId] = patched;
          plan.push({
            screenId: globalId,
            blockEntityId: item.blockEntityId,
            action: 'preserve',
            edgesUpdated: reconciled ? true : undefined,
            screenName,
            changeKinds: needsAssetPatch ? ['asset'] : undefined,
            assetSlots,
          });
        } else {
          plan.push({ screenId: globalId, blockEntityId: item.blockEntityId, action: 'preserve', screenName, assetSlots });
        }
        continue;
      }

      const conflict = Boolean(existingSimplified && !stableEquals(existingCanon, baseCanon));
      const changeKinds = classifyChangeKinds(existingCanon ?? baseCanon, strategyCanon, slotsDiverged);

      if (conflict) {
        const winner = input.resolutions?.screens?.[globalId] ?? 'funnel';
        if (winner === 'funnel') {
          const reconciled = reconcileStrategyCrossBlockEdges(
            existing,
            fresh,
            ownGlobalIds,
            strategyGlobalIndex,
            deleteSet,
          );
          if (reconciled || needsAssetPatch) {
            const patched = reconciled ?? cloneJson(existing);
            if (needsAssetPatch) {
              applyMergedAssetsToWrite(patched, fresh, existing, slotResolutions, locale, warnings);
            }
            writes[globalId] = patched;
          }
          plan.push({
            screenId: globalId,
            blockEntityId: item.blockEntityId,
            action: 'preserve',
            edgesUpdated: reconciled ? true : undefined,
            conflict: true,
            resolvedWith: 'funnel',
            screenName,
            changeKinds,
            assetSlots,
          });
          continue;
        }
        warnings.push(`Onboarding-side edits to screen ${globalId} were overwritten by strategy push.`);
        const next = withReplacedScreen(fresh, existing, strategySimplified);
        applyMergedAssetsToWrite(next, fresh, existing, slotResolutions, locale, warnings);
        writes[globalId] = next;
        plan.push({
          screenId: globalId,
          blockEntityId: item.blockEntityId,
          action: 'replace',
          conflict: true,
          resolvedWith: 'strategy',
          screenName,
          changeKinds,
          assetSlots,
        });
        continue;
      }

      const next = withReplacedScreen(fresh, existing, strategySimplified);
      applyMergedAssetsToWrite(next, fresh, existing, slotResolutions, locale, warnings);
      writes[globalId] = next;
      plan.push({
        screenId: globalId,
        blockEntityId: item.blockEntityId,
        action: 'replace',
        screenName,
        changeKinds,
        assetSlots,
      });
    }
  }

  for (const deletedId of deletes) {
    for (const [globalId, existing] of Object.entries(input.existingScreens)) {
      if (deleteSet.has(globalId) || writes[globalId]) continue;
      const targets = collectTriggerEdges(existing.content)
        .map((edge) => edge.nextScreenId)
        .filter((target): target is string => target === deletedId);
      if (targets.length > 0) {
        warnings.push(`Screen ${globalId} still has an edge to ${deletedId}, which this push deletes.`);
      }
    }
  }

  const metadata = mergePushManifestMetadata(input.flattened.manifest, input.existingMetadata, scopeSet, warnings);

  const launchGlobalId = String(input.flattened.flowDocument.launchScreenId || '');
  const launchBlockId = strategyGlobalIndex.get(launchGlobalId);
  const launchScreenId = launchBlockId && scopeSet.has(launchBlockId) ? launchGlobalId : undefined;

  return {
    writes,
    deletes,
    plan,
    metadata,
    launchScreenId,
    warnings,
    variableDiff,
    mergedVariables,
    forkedVariables,
    assetDiff: assetDiff.length > 0 ? assetDiff : undefined,
  };
}

function readScreenName(screen: StrategyFlowScreen | undefined): string | undefined {
  if (!screen) return undefined;
  return typeof screen.name === 'string' && screen.name ? screen.name : undefined;
}

/** Does the incoming side bring experiments/slots the target lacks? */
function sourceBringsAssets(entries: AiAssetSlotDiffEntry[]): boolean {
  return entries.some((entry) => (
    entry.status === 'source-only'
    || entry.sourceOnlyExperimentIds.length > 0
    || entry.conflictingExperimentIds.length > 0
  ));
}

/**
 * Merge both sides' aiAssets onto an outgoing write. Mirror ops apply ONLY to
 * slots the user explicitly resolved with a selectedExperimentId — a replace
 * must not resurrect an image the strategy deliberately removed from content.
 */
function applyMergedAssetsToWrite(
  next: StrategyFlowScreen,
  fresh: StrategyFlowScreen,
  existing: StrategyFlowScreen,
  slotResolutions: Record<string, AiAssetSlotResolution> | undefined,
  locale: string,
  warnings: string[],
): void {
  const sourceAssets = isRecord(fresh.aiAssets) ? fresh.aiAssets as Record<string, unknown> : undefined;
  const targetAssets = isRecord(existing.aiAssets) ? existing.aiAssets as Record<string, unknown> : undefined;
  if (!sourceAssets && !targetAssets) return;

  const merged = mergeAiAssetSlots({
    sourceAiAssets: sourceAssets,
    targetAiAssets: targetAssets,
    resolutions: slotResolutions,
    locale,
    stepKind: typeof next.screenType === 'string' ? next.screenType : undefined,
  });
  (next as Record<string, unknown>).aiAssets = merged.aiAssets;
  warnings.push(...merged.warnings);

  for (const op of merged.mirrorOps) {
    if (slotResolutions?.[op.slotKey]?.selectedExperimentId) {
      applyAiAssetMirrorOpToStep(next as Record<string, any>, op);
    }
  }
}

const EDGE_PATH_SEGMENTS = new Set([
  'nextscreenid', 'edges', 'conditions', 'transitionkind', 'trigger', 'triggers',
]);
const ASSET_PATH_SEGMENTS = new Set([
  'heroasset', 'featuredmediaasset', 'featuredmedia', 'image', 'imageurl',
  'generatedurl', 'media', 'asset', 'assets', 'icon', 'hero',
]);

/**
 * Classify what kind of content differs between two simplified projections by
 * walking their leaf-level differences and bucketing each path as edge / asset
 * / copy. Adds 'asset' when the aiAssets slot layer diverged too.
 */
function classifyChangeKinds(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined,
  slotsDiverged: boolean,
): StrategyPushChangeKind[] {
  const kinds = new Set<StrategyPushChangeKind>();
  if (slotsDiverged) kinds.add('asset');

  const paths: string[][] = [];
  collectLeafDiffPaths(before ?? {}, after ?? {}, [], paths);
  for (const path of paths) {
    const segments = path.map((segment) => segment.toLowerCase());
    if (segments.some((segment) => EDGE_PATH_SEGMENTS.has(segment))) {
      kinds.add('edge');
    } else if (segments.some((segment) => ASSET_PATH_SEGMENTS.has(segment))) {
      kinds.add('asset');
    } else {
      kinds.add('copy');
    }
  }

  const ordered: StrategyPushChangeKind[] = [];
  for (const kind of ['copy', 'edge', 'asset'] as const) {
    if (kinds.has(kind)) ordered.push(kind);
  }
  return ordered;
}

function collectLeafDiffPaths(
  before: unknown,
  after: unknown,
  path: string[],
  out: string[][],
): void {
  if (stableEquals(before, after)) return;

  const bothRecords = isRecord(before) && isRecord(after);
  const bothArrays = Array.isArray(before) && Array.isArray(after);
  if (!bothRecords && !bothArrays) {
    out.push(path.length > 0 ? path : ['<root>']);
    return;
  }

  if (bothArrays) {
    const maxLength = Math.max((before as unknown[]).length, (after as unknown[]).length);
    for (let index = 0; index < maxLength; index++) {
      collectLeafDiffPaths((before as unknown[])[index], (after as unknown[])[index], [...path, String(index)], out);
    }
    return;
  }

  const keys = new Set([
    ...Object.keys(before as Record<string, unknown>),
    ...Object.keys(after as Record<string, unknown>),
  ]);
  for (const key of keys) {
    collectLeafDiffPaths(
      (before as Record<string, unknown>)[key],
      (after as Record<string, unknown>)[key],
      [...path, key],
      out,
    );
  }
}

function withRefreshedAiContent(
  fresh: StrategyFlowScreen,
  strategySimplified: Record<string, unknown> | null,
): StrategyFlowScreen {
  const next = cloneJson(fresh);
  if (strategySimplified) next.aiContent = strategySimplified;
  return next;
}

function withReplacedScreen(
  fresh: StrategyFlowScreen,
  existing: StrategyFlowScreen,
  strategySimplified: Record<string, unknown> | null,
): StrategyFlowScreen {
  const next = withRefreshedAiContent(fresh, strategySimplified);
  if (isPosition(existing.position)) next.position = cloneJson(existing.position);
  return next;
}

interface StrategyCrossBlockEdge {
  actionKey?: string;
  edge: TriggerEdgeLike;
}

/**
 * Returns an edge-patched clone when the strategy-managed cross-block edges of an otherwise
 * preserved screen differ from the freshly compiled ones, or null when nothing changed.
 * Intra-block edges and user edges to non-strategy screens are kept untouched.
 */
function reconcileStrategyCrossBlockEdges(
  existing: StrategyFlowScreen,
  fresh: StrategyFlowScreen,
  ownGlobalIds: Set<string>,
  strategyGlobalIndex: Map<string, string>,
  deletedIds: Set<string>,
): StrategyFlowScreen | null {
  const isStrategyCrossBlock = (target: string | undefined): boolean => Boolean(
    target
    && !ownGlobalIds.has(target)
    && (strategyGlobalIndex.has(target) || deletedIds.has(target)),
  );

  const desired = collectCrossBlockEdges(fresh, isStrategyCrossBlock)
    .filter((entry) => !deletedIds.has(String(entry.edge.nextScreenId)));
  const current = collectCrossBlockEdges(existing, isStrategyCrossBlock);

  const signature = (entries: StrategyCrossBlockEdge[]): string => entries
    .map((entry) => [
      entry.actionKey ?? '',
      entry.edge.nextScreenId ?? '',
      entry.edge.transitionKind ?? '',
      stableStringify(entry.edge.conditions ?? []),
    ].join('|'))
    .sort()
    .join('\n');

  if (signature(desired) === signature(current)) return null;

  const patched = cloneJson(existing);
  if (!isRecord(patched.content)) return null;

  rewriteTriggerEdgesInPlace(patched.content, (edge) => (
    isStrategyCrossBlock(edge.nextScreenId) ? null : edge
  ));
  for (const entry of desired) {
    appendTriggerEdgeAtActionKeyInPlace(patched.content, entry.actionKey, cloneJson(entry.edge));
  }
  return patched;
}

function collectCrossBlockEdges(
  screen: StrategyFlowScreen,
  isStrategyCrossBlock: (target: string | undefined) => boolean,
): StrategyCrossBlockEdge[] {
  if (!isRecord(screen.content)) return [];
  const entries: StrategyCrossBlockEdge[] = [];
  rewriteTriggerEdgesInPlace(cloneJson(screen.content), (edge, context) => {
    if (isStrategyCrossBlock(edge.nextScreenId)) {
      entries.push({ actionKey: context.actionKey, edge });
    }
    return edge;
  });
  return entries;
}

function mergePushManifestMetadata(
  freshManifest: StrategyFlowManifest,
  existingMetadata: Record<string, unknown> | undefined,
  scopeSet: Set<string>,
  warnings: string[],
): Record<string, string> {
  const existingManifest = parseManifest(existingMetadata);
  const rawManifestRecord = readRawManifestRecord(existingMetadata);

  let mergedBlocks: StrategyBlockManifestItem[];
  if (existingManifest) {
    const freshByEntity = new Map(
      freshManifest.blocks
        .filter((item) => scopeSet.has(item.blockEntityId))
        .map((item) => [item.blockEntityId, item]),
    );
    mergedBlocks = existingManifest.blocks.map((item) => freshByEntity.get(item.blockEntityId) ?? item);
    for (const item of freshManifest.blocks) {
      if (scopeSet.has(item.blockEntityId)
        && !existingManifest.blocks.some((candidate) => candidate.blockEntityId === item.blockEntityId)) {
        mergedBlocks.push(item);
      }
    }
  } else {
    mergedBlocks = freshManifest.blocks.filter((item) => scopeSet.has(item.blockEntityId));
    if (mergedBlocks.length !== freshManifest.blocks.length) {
      warnings.push('Existing transform manifest missing or unreadable; wrote a scope-only manifest.');
    }
  }

  const mergedManifest: Record<string, unknown> = {
    ...(rawManifestRecord ?? {}),
    ...freshManifest,
    blocks: mergedBlocks,
  };

  // C9-S4: an explicitly scoped push re-includes a compile-excluded block —
  // remove it from the carried exclusion list; drop the key when it empties.
  const carriedExcluded = Array.isArray(rawManifestRecord?.excludedBlockEntityIds)
    ? (rawManifestRecord!.excludedBlockEntityIds as unknown[]).map(String).filter((id) => !scopeSet.has(id))
    : [];
  if (carriedExcluded.length > 0) {
    mergedManifest.excludedBlockEntityIds = carriedExcluded;
  } else {
    delete mergedManifest.excludedBlockEntityIds;
  }

  const metadata = stringifyMetadata(existingMetadata);
  metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY] = JSON.stringify(mergedManifest);
  return metadata;
}

function readRawManifestRecord(metadata: unknown): Record<string, unknown> | undefined {
  if (!isRecord(metadata)) return undefined;
  const raw = metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY];
  if (!raw) return undefined;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Projects a simplified-content snapshot into the block-LOCAL edge namespace for
 * diffing: in-block global targets are remapped to local ids, edges to screens
 * outside the block (cross-block sequence edges, funnel-only wiring) are dropped,
 * and unrecognized targets are kept as-is on both sides. Baselines stamped at
 * compile time carry GLOBAL edge ids while push-side projections carry local
 * ones — without a shared canon every edge-bearing screen reads as edited on
 * both sides forever (C9-S1 phantom conflicts).
 */
function canonicalizeSimplifiedForDiff(
  content: Record<string, unknown> | null | undefined,
  globalToLocal: Map<string, string>,
  knownGlobalIds: Set<string>,
): Record<string, unknown> | null {
  if (!isRecord(content)) return null;
  const clone = cloneJson(content) as Record<string, unknown>;
  rewriteTriggerEdgesInPlace(clone, (edge) => {
    const target = edge.nextScreenId;
    if (!target) return edge;
    const local = globalToLocal.get(target);
    if (local) return { ...edge, nextScreenId: local };
    if (knownGlobalIds.has(target)) return null;
    return edge;
  });
  remapConditionKeysInPlace(clone, globalToLocal);
  return clone;
}

function stableEquals(a: unknown, b: unknown): boolean {
  return stableStringify(a) === stableStringify(b);
}

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(',')}]`;
  }
  if (isRecord(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${stableStringify(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'undefined';
}

function normalizeBundle(input: StrategyFlowBlock | StrategyBlockScreenBundle): StrategyBlockScreenBundle {
  if (isBundle(input)) {
    return {
      block: input.block,
      screens: Array.isArray(input.screens)
        ? input.screens
        : Array.isArray(input.block.screens)
          ? input.block.screens
          : [],
    };
  }
  return {
    block: input as StrategyFlowBlock,
    screens: Array.isArray((input as StrategyFlowBlock).screens)
      ? (input as StrategyFlowBlock).screens as StrategyFlowScreen[]
      : [],
  };
}

function isBundle(input: StrategyFlowBlock | StrategyBlockScreenBundle): input is StrategyBlockScreenBundle {
  return isRecord((input as StrategyBlockScreenBundle).block);
}

function prepareBlock(
  block: StrategyFlowBlock,
  screens: StrategyFlowScreen[] = [],
  index: number,
  strategyId: string,
): PreparedBlock {
  const blockEntityId = getBlockEntityId(block) || `block_${index + 1}`;
  const blockId = typeof block.id === 'string' ? block.id : undefined;
  const localToGlobal = new Map<string, string>();
  const globalToLocal = new Map<string, string>();
  const usedGlobalIds = new Set<string>();

  screens.forEach((screen, screenIndex) => {
    const localId = getScreenId(screen) || `screen_${screenIndex + 1}`;
    const baseGlobalId = toGlobalScreenId(blockEntityId, localId);
    let globalId = baseGlobalId;
    let suffix = 2;
    while (usedGlobalIds.has(globalId)) {
      globalId = `${baseGlobalId}_${suffix}`;
      suffix += 1;
    }
    usedGlobalIds.add(globalId);
    localToGlobal.set(localId, globalId);
    globalToLocal.set(globalId, localId);
  });

  const entryLocalId = resolveEntryScreenId(block, screens);
  const exitLocalId = block.compileHints?.exitScreenId ?? inferExitScreenId(screens, entryLocalId);
  const exitLocalIds = resolveExitScreenIds(block, screens, exitLocalId);

  return {
    block: {
      ...block,
      entityId: blockEntityId,
      screens,
      compileHints: compactCompileHints({
        ...(block.compileHints ?? {}),
        entryScreenId: entryLocalId,
        exitScreenId: exitLocalId,
        exits: exitLocalIds.length > 0 ? exitLocalIds.map((screenId) => ({ screenId })) : undefined,
      }),
    },
    blockId,
    blockEntityId,
    screens: normalizeStrategyBlocksForFlowTransform([{ ...block, entityId: blockEntityId, screens }], strategyId)[0].screens ?? screens,
    localToGlobal,
    globalToLocal,
    entryLocalId,
    entryGlobalId: entryLocalId ? localToGlobal.get(entryLocalId) : undefined,
    exitLocalId,
    exitGlobalId: exitLocalId ? localToGlobal.get(exitLocalId) : undefined,
    exitLocalIds,
    exitGlobalIds: exitLocalIds
      .map((localId) => localToGlobal.get(localId))
      .filter((globalId): globalId is string => Boolean(globalId)),
  };
}

/**
 * All exit screens of a block (B1-W5): hinted exits (stale ids filtered) win;
 * else the UNION of screens tagged aiMeta.screenRole 'exit' and every
 * intra-block leaf. The union matters: generators tag ONE screen as 'exit'
 * while sibling variants stay 'body' leaves — role-only pooling stranded
 * those variants (measured: 4 on the first regenerated gate artifact).
 * The legacy single pick stays first for byte-stable manifests.
 */
function resolveExitScreenIds(
  block: StrategyFlowBlock,
  screens: StrategyFlowScreen[],
  primaryExitId?: string,
): string[] {
  const screenIds = new Set<string>(screens.map(getScreenId).filter((id): id is string => Boolean(id)));

  const hinted = (block.compileHints?.exits ?? [])
    .map((exit) => exit.screenId)
    .filter((screenId) => screenIds.has(screenId));

  const byRole = screens
    .filter((screen) => isRecord(screen.aiMeta) && screen.aiMeta.screenRole === 'exit')
    .map(getScreenId)
    .filter((id): id is string => Boolean(id));

  const leaves = screens
    .filter((screen) => {
      const targets = collectTriggerEdges(screen.content)
        .map((edge: TriggerEdgeLike) => edge.nextScreenId)
        .filter((target): target is string => typeof target === 'string' && screenIds.has(target));
      return targets.length === 0;
    })
    .map(getScreenId)
    .filter((id): id is string => Boolean(id));

  const pool = hinted.length > 0 ? hinted : [...byRole, ...leaves];
  const ordered = primaryExitId && screenIds.has(primaryExitId)
    ? [primaryExitId, ...pool.filter((id) => id !== primaryExitId)]
    : pool;
  return [...new Set(ordered)];
}

/** Intra-block successors of a screen (targets that stay inside the block). */
function inBlockTargets(screen: StrategyFlowScreen, screenIds: Set<string>): string[] {
  return collectTriggerEdges(screen.content)
    .map((edge: TriggerEdgeLike) => edge.nextScreenId)
    .filter((target: string | undefined): target is string => (
      typeof target === 'string' && target.length > 0 && screenIds.has(target)
    ));
}

/** Screens the visitor can actually reach from the block entry, intra-block. */
function reachableWithinBlock(
  screens: StrategyFlowScreen[],
  screenIds: Set<string>,
  entryScreenId?: string,
): Set<string> {
  const byId = new Map<string, StrategyFlowScreen>();
  for (const screen of screens) {
    const id = getScreenId(screen);
    if (id) byId.set(id, screen);
  }
  const start = entryScreenId && byId.has(entryScreenId) ? entryScreenId : getScreenId(screens[0]);
  const reached = new Set<string>();
  const queue: string[] = start ? [start] : [];
  while (queue.length > 0) {
    const current = queue.shift() as string;
    if (reached.has(current)) continue;
    reached.add(current);
    const screen = byId.get(current);
    if (!screen) continue;
    for (const target of inBlockTargets(screen, screenIds)) {
      if (!reached.has(target)) queue.push(target);
    }
  }
  return reached;
}

function inferExitScreenId(screens: StrategyFlowScreen[], entryScreenId?: string): string | undefined {
  const byRole = screens.find((screen) => isRecord(screen.aiMeta) && screen.aiMeta.screenRole === 'exit');
  if (byRole) return getScreenId(byRole);

  const screenIds = new Set<string>(screens.map(getScreenId).filter((id): id is string => Boolean(id)));
  const leaf = [...screens].reverse().find((screen) => inBlockTargets(screen, screenIds).length === 0);
  const leafId = getScreenId(leaf);
  if (leafId) return leafId;

  // D-W6-4. No leaf: the block graph is a cycle (the W6 fasting `step_06` is
  // 41 → 42, 42 ↔ 43). Falling through to the ENTRY screen parked the
  // block-boundary edge behind that screen's own unconditional edge, where the
  // engine can never take it — the paywall block was unsellable while the
  // healthcheck read `84/84 reachable`. The screen where the visitor actually
  // STOPS is the one whose preferred trigger is terminal (no edges);
  // `makeTriggerNavigable` flips its `complete` kind to `advance` the moment it
  // gains the boundary edge. Reachable candidates win over orphans, and the
  // LAST in document order breaks a tie — the same order the leaf search uses.
  const terminal = screens.filter((screen) => hasTerminalPreferredTrigger(screen.content));
  if (terminal.length > 0) {
    const reached = reachableWithinBlock(screens, screenIds, entryScreenId);
    const pool = terminal.filter((screen) => {
      const id = getScreenId(screen);
      return Boolean(id) && reached.has(id as string);
    });
    const chosen = (pool.length > 0 ? pool : terminal)[(pool.length > 0 ? pool : terminal).length - 1];
    const chosenId = getScreenId(chosen);
    if (chosenId) return chosenId;
  }

  return entryScreenId || getScreenId(screens[screens.length - 1]);
}

function resolveEntryScreenId(block: StrategyFlowBlock, screens: StrategyFlowScreen[]): string | undefined {
  const hinted = block.compileHints?.entryScreenId;
  if (hinted && screens.some((screen) => getScreenId(screen) === hinted)) return hinted;
  if (block.firstScreenId && screens.some((screen) => getScreenId(screen) === block.firstScreenId)) return block.firstScreenId;
  return getScreenId(screens[0]);
}

function resolveSourceGlobalId(block: PreparedBlock, edge: BlockEdgeLike): string | undefined {
  if (edge.sourceScreenId && block.localToGlobal.has(edge.sourceScreenId)) {
    return block.localToGlobal.get(edge.sourceScreenId);
  }
  return block.exitGlobalId ?? block.entryGlobalId;
}

/**
 * B1-W5 fan-out: an explicit sourceScreenId anchors exactly there; a
 * block-level edge (no source) anchors to EVERY exit screen so all exit
 * variants continue into the next block.
 */
function resolveSourceGlobalIds(block: PreparedBlock, edge: BlockEdgeLike): string[] {
  if (edge.sourceScreenId && block.localToGlobal.has(edge.sourceScreenId)) {
    return [block.localToGlobal.get(edge.sourceScreenId)!];
  }
  if (block.exitGlobalIds.length > 0) return block.exitGlobalIds;
  const single = block.exitGlobalId ?? block.entryGlobalId;
  return single ? [single] : [];
}

function resolveTargetGlobalId(block: PreparedBlock | undefined, edge: BlockEdgeLike): string | undefined {
  if (!block) return undefined;
  if (edge.targetScreenId && block.localToGlobal.has(edge.targetScreenId)) {
    return block.localToGlobal.get(edge.targetScreenId);
  }
  return block.entryGlobalId;
}

function inferScreenRole(
  localId: string,
  entryScreenId?: string,
  exitScreenId?: string,
): 'entry' | 'body' | 'exit' {
  if (localId === entryScreenId) return 'entry';
  if (localId === exitScreenId) return 'exit';
  return 'body';
}

function buildScreenToBlockIndexFromItems(items: StrategyBlockManifestItem[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const item of items) {
    for (const globalId of Object.values(item.screenIdMap)) {
      index.set(globalId, item.blockEntityId);
    }
  }
  return index;
}

function buildGlobalToLocalIndex(items: StrategyBlockManifestItem[]): Map<string, string> {
  const index = new Map<string, string>();
  for (const item of items) {
    for (const [localId, globalId] of Object.entries(item.screenIdMap)) {
      index.set(globalId, localId);
    }
  }
  return index;
}

function resolveLiftManifestBlocks(
  screens: Record<string, StrategyFlowScreen>,
  manifest: StrategyFlowManifest | undefined,
  strategyId: string,
  launchScreenId?: string,
  liftTargetStrategyId?: string,
  warnings?: string[],
  foreignTagIgnored?: StrategyLiftForeignTagIgnored[],
): StrategyBlockManifestItem[] {
  if (manifest?.blocks?.length && (!hasAnyBlockTags(screens) || manifestMatchesCurrentTags(screens, manifest))) {
    // The fast path used to return manifest.blocks verbatim, which SILENTLY
    // DROPPED any screen absent from every screenIdMap (an untagged screen
    // added to the funnel after compile). Those screens are untagged by
    // construction here — a tagged-but-unmapped screen fails the tag match
    // above — so they lift into the Loose Screens group instead of vanishing.
    return appendUncoveredScreensAsLoose(manifest.blocks, screens, warnings);
  }
  return inferManifestBlocksFromTags(screens, strategyId, manifest, launchScreenId, liftTargetStrategyId, warnings, foreignTagIgnored);
}

function appendUncoveredScreensAsLoose(
  items: StrategyBlockManifestItem[],
  screens: Record<string, StrategyFlowScreen>,
  warnings?: string[],
): StrategyBlockManifestItem[] {
  const covered = new Set<string>();
  for (const item of items) {
    for (const globalId of Object.values(item.screenIdMap ?? {})) covered.add(String(globalId));
  }
  const uncovered = Object.keys(screens).filter((screenId) => !covered.has(screenId));
  if (uncovered.length === 0) return items;

  const screenIdMap: Record<string, string> = {};
  for (const globalId of uncovered) {
    const preferred = stripGlobalScreenId(globalId, '_untagged')
      ?? String(screens[globalId]?.aiMeta?.localScreenId || globalId);
    screenIdMap[screenIdMap[preferred] !== undefined ? globalId : preferred] = globalId;
  }
  warnings?.push(
    `${uncovered.length} screen(s) not covered by the transform manifest lifted into the Loose Screens group: `
    + `${uncovered.slice(0, 5).join(', ')}${uncovered.length > 5 ? ', …' : ''}.`,
  );
  return [
    ...items,
    { blockEntityId: '_untagged', name: 'Loose Screens', screenIdMap, order: items.length },
  ];
}

function inferManifestBlocksFromTags(
  screens: Record<string, StrategyFlowScreen>,
  strategyId: string,
  manifest?: StrategyFlowManifest,
  launchScreenId?: string,
  liftTargetStrategyId?: string,
  warnings?: string[],
  foreignTagIgnored?: StrategyLiftForeignTagIgnored[],
): StrategyBlockManifestItem[] {
  const byBlock = new Map<string, Record<string, string>>();
  const screenToBlock = new Map<string, string>();
  const effectiveBlockTagByScreen = new Map<string, string | undefined>();

  for (const [screenId, screen] of Object.entries(screens)) {
    let blockTag = readBlockTag(screen);
    // A block: tag is active grouping metadata only for the lift target strategy.
    // Screens pasted from another strategy keep strategy:{other} + block:{x} tags;
    // grouping them here would contaminate the target strategy with foreign blocks.
    // Screens with a block: tag but NO strategy: tag still group (manual tagging
    // before the onboarding was associated with a strategy).
    const strategyTag = screen.tags
      ?.find((tag) => tag.startsWith(STRATEGY_TAG_PREFIX))
      ?.slice(STRATEGY_TAG_PREFIX.length);
    if (blockTag && liftTargetStrategyId && strategyTag && strategyTag !== liftTargetStrategyId) {
      warnings?.push(
        `Ignored block tag block:${blockTag} on screen ${screenId}: screen belongs to strategy ${strategyTag}, lifting into ${liftTargetStrategyId}.`,
      );
      foreignTagIgnored?.push({ screenId, blockTag, strategyTag });
      blockTag = undefined;
    }
    effectiveBlockTagByScreen.set(screenId, blockTag);
  }

  const hasBlockTags = Array.from(effectiveBlockTagByScreen.values()).some((tag) => tag !== undefined);

  for (const [screenId, screen] of Object.entries(screens)) {
    const blockTag = effectiveBlockTagByScreen.get(screenId);
    const blockEntityId = blockTag
      ?? (hasBlockTags ? '_untagged' : manifest?.blocks?.[0]?.blockEntityId ?? 'onboarding_block');
    const localId = stripGlobalScreenId(screenId, blockEntityId) ?? String(screen.aiMeta?.localScreenId || screenId);
    if (!byBlock.has(blockEntityId)) byBlock.set(blockEntityId, {});
    byBlock.get(blockEntityId)![localId] = screenId;
    screenToBlock.set(screenId, blockEntityId);
  }

  const launchBlockId = launchScreenId ? screenToBlock.get(launchScreenId) : undefined;
  const orderedBlockIds = orderInferredBlockIds(byBlock, screenToBlock, screens, launchBlockId);

  return orderedBlockIds.map((blockEntityId, index) => {
    const fromManifest = blockDataFromManifest(blockEntityId, manifest);
    const isLaunchBlock = launchBlockId ? blockEntityId === launchBlockId : index === 0;

    return {
      ...fromManifest,
      blockEntityId,
      name: fromManifest?.name ?? (blockEntityId === '_untagged' ? 'Loose Screens' : hasBlockTags ? blockEntityId : 'Onboarding Block'),
      order: index,
      isFirstBlock: isLaunchBlock,
      isLaunchBlock,
      screenIdMap: byBlock.get(blockEntityId) ?? {},
      compileHints: {},
      blockData: {
        ...fromManifest?.blockData,
        strategyId,
      },
    };
  });
}

function orderInferredBlockIds(
  byBlock: Map<string, Record<string, string>>,
  screenToBlock: Map<string, string>,
  screens: Record<string, StrategyFlowScreen>,
  launchBlockId?: string,
): string[] {
  const blockIds = Array.from(byBlock.keys());
  const originalIndex = new Map(blockIds.map((blockId, index) => [blockId, index]));
  const graph = new Map<string, Set<string>>(blockIds.map((blockId) => [blockId, new Set<string>()]));

  for (const [screenId, screen] of Object.entries(screens)) {
    const sourceBlockId = screenToBlock.get(screenId);
    if (!sourceBlockId) continue;

    for (const edge of collectTriggerEdges(screen.content)) {
      const targetBlockId = edge.nextScreenId ? screenToBlock.get(edge.nextScreenId) : undefined;
      if (targetBlockId && targetBlockId !== sourceBlockId) {
        graph.get(sourceBlockId)?.add(targetBlockId);
      }
    }
  }

  const depthByBlock = new Map<string, number>();
  if (launchBlockId && graph.has(launchBlockId)) {
    const queue: Array<{ blockId: string; depth: number }> = [{ blockId: launchBlockId, depth: 0 }];
    depthByBlock.set(launchBlockId, 0);

    while (queue.length > 0) {
      const { blockId, depth } = queue.shift()!;
      for (const childId of graph.get(blockId) ?? []) {
        if (depthByBlock.has(childId)) continue;
        depthByBlock.set(childId, depth + 1);
        queue.push({ blockId: childId, depth: depth + 1 });
      }
    }
  }

  return [...blockIds].sort((a, b) => {
    const depthDelta = (depthByBlock.get(a) ?? Number.MAX_SAFE_INTEGER)
      - (depthByBlock.get(b) ?? Number.MAX_SAFE_INTEGER);
    if (depthDelta !== 0) return depthDelta;

    const positionA = getBlockScreenMinPosition(byBlock.get(a), screens);
    const positionB = getBlockScreenMinPosition(byBlock.get(b), screens);
    if (positionA.x !== positionB.x) return positionA.x - positionB.x;
    if (positionA.y !== positionB.y) return positionA.y - positionB.y;

    return (originalIndex.get(a) ?? 0) - (originalIndex.get(b) ?? 0);
  });
}

function getBlockScreenMinPosition(
  screenIdMap: Record<string, string> | undefined,
  screens: Record<string, StrategyFlowScreen>,
): { x: number; y: number } {
  const positions = Object.values(screenIdMap ?? {})
    .map((screenId) => screens[screenId]?.position)
    .filter(isPosition);

  if (positions.length === 0) {
    return { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER };
  }

  return {
    x: Math.min(...positions.map((position) => position.x)),
    y: Math.min(...positions.map((position) => position.y)),
  };
}

function hasAnyBlockTags(screens: Record<string, StrategyFlowScreen>): boolean {
  return Object.values(screens).some((screen) => readBlockTag(screen) !== undefined);
}

function manifestMatchesCurrentTags(
  screens: Record<string, StrategyFlowScreen>,
  manifest: StrategyFlowManifest,
): boolean {
  const manifestIndex = buildScreenToBlockIndexFromItems(manifest.blocks);
  for (const [screenId, screen] of Object.entries(screens)) {
    const blockTag = readBlockTag(screen);
    if (blockTag && manifestIndex.get(screenId) !== blockTag) {
      return false;
    }
  }
  return true;
}

function readBlockTag(screen: StrategyFlowScreen): string | undefined {
  return screen.tags?.find((tag) => tag.startsWith(STRATEGY_BLOCK_TAG_PREFIX))
    ?.slice(STRATEGY_BLOCK_TAG_PREFIX.length);
}

function blockDataFromManifest(
  blockEntityId: string,
  manifest?: StrategyFlowManifest,
): StrategyBlockManifestItem | undefined {
  return manifest?.blocks?.find((block) => block.blockEntityId === blockEntityId);
}

function deriveLiftedBlockPosition(
  blockBase: Record<string, unknown>,
  globalIds: string[],
  screens: Record<string, StrategyFlowScreen>,
  index: number,
): { x: number; y: number } {
  if (isPosition(blockBase.position)) {
    return cloneJson(blockBase.position);
  }

  const positions = globalIds
    .map((globalId) => screens[globalId]?.position)
    .filter(isPosition);
  if (positions.length > 0) {
    return {
      x: Math.min(...positions.map((position) => position.x)),
      y: Math.min(...positions.map((position) => position.y)),
    };
  }

  return {
    x: index * 562,
    y: 0,
  };
}

function ensureLiftedBlockPositions(blocks: StrategyFlowBlock[]): void {
  if (blocks.every((block) => isPosition(block.position))) return;

  const positionable = blocks.map((block) => ({
    entityId: getBlockEntityId(block) || '',
    isFirstBlock: block.isFirstBlock,
    isLaunchBlock: block.isLaunchBlock,
    action: block.action,
    position: block.position,
  })).filter((block) => block.entityId);

  calculateBlockPositions(positionable);
  const byId = new Map(positionable.map((block) => [block.entityId, block.position]));

  blocks.forEach((block, index) => {
    if (isPosition(block.position)) return;
    const position = byId.get(getBlockEntityId(block) || '');
    block.position = isPosition(position) ? position : { x: index * 562, y: 0 };
  });
}

function toGlobalScreenId(blockEntityId: string, localScreenId: string): string {
  return localScreenId.startsWith(`${blockEntityId}__`)
    ? localScreenId
    : `${blockEntityId}__${localScreenId}`;
}

function stripGlobalScreenId(globalScreenId: string, blockEntityId: string): string | undefined {
  const prefix = `${blockEntityId}__`;
  return globalScreenId.startsWith(prefix) ? globalScreenId.slice(prefix.length) : undefined;
}

function getScreenId(screen?: StrategyFlowScreen): string {
  if (!screen) return '';
  return String(screen.aiMeta?.localScreenId || screen.id || screen.entityId || '').trim();
}

function readEdgeConditions(edge: TriggerEdgeLike | BlockEdgeLike): Array<Record<string, unknown>> {
  if (Array.isArray(edge.conditions)) return cloneJson(edge.conditions);
  const rule = (edge as { rule?: Array<Record<string, unknown>> }).rule;
  return Array.isArray(rule) ? cloneJson(rule) : [];
}

function dedupeBlockTargets(
  targets: BlockBoundaryTarget[],
): BlockBoundaryTarget[] {
  const seen = new Set<string>();
  return targets.filter((target) => {
    const key = [
      target.targetEntityId,
      target.sourceScreenId ?? '',
      target.sourceActionKey ?? '',
      target.targetScreenId ?? '',
      target.transitionKind ?? '',
      JSON.stringify(target.conditions ?? []),
    ].join(':');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function remapConditionKeysInArray(
  conditions: Array<Record<string, unknown>>,
  idMap: Map<string, string>,
): Array<Record<string, unknown>> {
  if (conditions.length === 0 || idMap.size === 0) return conditions;

  return conditions.map((condition) => {
    if (typeof condition.key !== 'string') return condition;
    const key = remapConditionKey(condition.key, idMap);
    return key === condition.key ? condition : { ...condition, key };
  });
}

function parseManifest(metadata: unknown): StrategyFlowManifest | undefined {
  if (!isRecord(metadata)) return undefined;
  const raw = metadata[STRATEGY_BLOCKS_MANIFEST_METADATA_KEY];
  if (!raw) return undefined;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (isRecord(parsed) && parsed.version === 1 && Array.isArray(parsed.blocks)) {
      return parsed as unknown as StrategyFlowManifest;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function stringifyMetadata(metadata?: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(metadata ?? {})) {
    out[key] = typeof value === 'string' ? value : JSON.stringify(value);
  }
  return out;
}

function stripLargeBlockFields(block: StrategyFlowBlock): Record<string, unknown> {
  const { screens, action, ...rest } = block;
  const cloned = cloneJson(rest) as Record<string, unknown>;
  const omitted: string[] = [];
  for (const key of MANIFEST_HEAVY_BLOCK_DATA_KEYS) {
    if (key in cloned) {
      delete cloned[key];
      omitted.push(key);
    }
  }
  if (omitted.length > 0) {
    cloned[MANIFEST_OMITTED_HEAVY_KEYS_FIELD] = omitted;
  }
  return cloned;
}

/**
 * The origin the flatten uses to place a block's screens on the flow canvas:
 * the block's explicit canvas position, or the index-based auto-layout
 * fallback. Single source for the flatten shift AND the manifest persistence
 * (blockData.position) that lets the lift invert it exactly.
 */
function resolveBlockOrigin(block: StrategyFlowBlock, index: number): { x: number; y: number } {
  return isPosition(block.position) ? block.position : { x: index * 720, y: 0 };
}

function compactCompileHints(hints: StrategyBlockCompileHints): StrategyBlockCompileHints {
  return Object.fromEntries(
    Object.entries(hints).filter(([, value]) => value !== undefined),
  ) as StrategyBlockCompileHints;
}

function filterExitHintsToScreens(
  exits: StrategyBlockCompileHints['exits'],
  screens: StrategyFlowScreen[],
): StrategyBlockCompileHints['exits'] {
  if (!Array.isArray(exits) || exits.length === 0) return undefined;
  const screenIds = new Set(screens.map(getScreenId).filter(Boolean));
  const kept = exits.filter((exit) => screenIds.has(exit.screenId));
  return kept.length > 0 ? kept : undefined;
}

function mergeTags(existing: unknown, additions: string[]): string[] {
  const set = new Set<string>(
    Array.isArray(existing)
      ? existing.filter((tag): tag is string => typeof tag === 'string' && !isActiveStrategyMembershipTag(tag))
      : [],
  );
  additions.filter(Boolean).forEach((tag) => set.add(tag));
  return Array.from(set);
}

function isActiveStrategyMembershipTag(tag: string): boolean {
  return tag.startsWith(STRATEGY_TAG_PREFIX)
    || tag.startsWith(STRATEGY_BLOCK_TAG_PREFIX)
    || tag.startsWith(STRATEGY_BLOCK_DOC_TAG_PREFIX);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isPosition(value: unknown): value is { x: number; y: number } {
  return isRecord(value) && typeof value.x === 'number' && typeof value.y === 'number';
}

function cloneJson<T>(value: T): T {
  return value === undefined ? value : JSON.parse(JSON.stringify(value));
}
